import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings, Users, GraduationCap, BookOpen, Award, Send, Mail,
  Calendar, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Save, Loader2,
  Upload, Download, Search, X, Check, AlertCircle,
  Eye, EyeOff, RefreshCw, ImagePlus, Trash, Building2, ShieldCheck, LayoutGrid, Shield,
  Fingerprint, Wifi, WifiOff, Link, Unlink, Play, RotateCcw,
  GitBranch, Network, HardDrive, Cloud, Database,
} from 'lucide-react';
import { schoolConfigAPI, rolesAPI, authAPI, biometricAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import RoleManagement from './RoleManagement';
import UserAccessControl from './UserAccessControl';
import toast from 'react-hot-toast';

// ── File import sub-component (keeps hooks at component top-level) ──
function FileImportSection() {
  const [fileImporting, setFileImporting] = useState(false);
  const [fileResult,    setFileResult]    = useState(null);
  const fileRef = useRef(null);

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setFileImporting(true); setFileResult(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await biometricAPI.importDeviceFile(fd);
      setFileResult({ success: true, ...r.data });
      toast.success(`${r.data.synced} attendance records imported`);
    } catch(err) {
      setFileResult({ success: false, error: err.response?.data?.error || 'Import failed' });
    } finally { setFileImporting(false); e.target.value = ''; }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <label className={`flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl cursor-pointer transition-colors ${fileImporting ? 'opacity-60' : ''}`}>
          {fileImporting ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
          {fileImporting ? 'Importing…' : 'Select & Import File'}
          <input ref={fileRef} type="file" accept=".dat,.xls,.xlsx,.csv,.txt,.log" className="hidden" onChange={handleFileImport} disabled={fileImporting}/>
        </label>
        <span className="text-xs text-slate-400">.dat · .xls · .xlsx · .csv · .txt</span>
      </div>
      {fileResult && (
        <div className={`rounded-xl p-3 text-xs ${fileResult.success ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {fileResult.success
            ? `✅ ${fileResult.message} · ${fileResult.unmatched > 0 ? fileResult.unmatched + ' unmatched (set Biometric Code in Employee Mapping tab)' : ''}`
            : `❌ ${fileResult.error}`}
        </div>
      )}
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700">{message}</p>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg, type = 'success', onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
      ${type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
      {type === 'success' ? <Check size={16} /> : <X size={16} />}
      {msg}
    </div>
  );
}

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const inp = "text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white";
const fi  = `${inp} w-full`;

// FS must be defined at MODULE LEVEL – never inside another component.
// Defining it inside a component recreates the function reference on every render,
// which causes React to unmount/remount the input and lose cursor focus.
function FS({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

const TABS = [
  { key:'staff',        label:'Employee Info',        icon:Users },
  { key:'students',     label:'Students',            icon:GraduationCap },
  { key:'classes',      label:'Grade & Section',     icon:LayoutGrid },
  { key:'academic',     label:'Academic',            icon:BookOpen },
  { key:'departments',  label:'Departments',         icon:Building2 },
  { key:'subjects',     label:'Subjects',            icon:BookOpen },
  { key:'designations', label:'Designations',        icon:Award },
  { key:'roles',          label:'User Types',             icon:Shield },
  { key:'subjectteacher', label:'Subject Teacher Assignment', icon:BookOpen },
  { key:'telegram',     label:'Telegram Bot',        icon:Send },
  { key:'email',        label:'Email Config',        icon:Mail },
  { key:'holidays',          label:'Holiday Calendar',       icon:Calendar },
  { key:'user_access',         label:'User Access Control',  icon:Shield },
  { key:'storage',             label:'Storage',              icon:HardDrive },
];

// ═══════════════════════════════════════════════════════════
// TAB: STAFF
// ═══════════════════════════════════════════════════════════
function StaffTab({ toast }) {
  const [staff, setStaff]           = useState([]);
  const [departments, setDepts]     = useState([]);
  const [designations, setDesigs]   = useState([]);
  const [subjects, setSubjects]     = useState([]);
  const [roles, setRoles]           = useState([]);
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importing, setImporting]   = useState(false);

  const empty = {
    employeeCode:'', name:'', email:'', departmentId:'', designationId:'',
    designation:'', subject:'', staffType:'teacher', phone:'', joinDate:'',
    telegramChatId:'', biometricUid:'',
    gender:'', dateOfBirth:'', reportingAuthorityId:'',
    createLogin:false, password:'Admin@123', roleId:'',
    isObserver: false,
    photoUrl: '', signatureUrl: '',
    bio: '',
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, des, sub, rol] = await Promise.all([
        schoolConfigAPI.getStaff({ staffType: typeFilter||undefined }),
        schoolConfigAPI.getDepartments(),
        schoolConfigAPI.getDesignations(),
        schoolConfigAPI.getSubjects(),
        rolesAPI.getAll(),
      ]);
      setStaff(s.data);
      setDepts(d.data);
      setDesigs(des.data);
      setSubjects(sub.data);
      setRoles(rol.data);
    } catch(e) { toast(e.response?.data?.error||e.message, 'error'); }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.email||'').toLowerCase().includes(search.toLowerCase()) ||
    (s.subject||'').toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    try {
      if (editing) await schoolConfigAPI.updateStaff(editing.id, form);
      else         await schoolConfigAPI.createStaff(form);
      toast(editing ? 'Staff member updated' : 'Staff member added');
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch(e) { toast(e.response?.data?.error||e.message, 'error'); }
  };

  const handleToggle = async (id) => {
    try { await schoolConfigAPI.toggleStaff(id); load(); } catch(e) { toast(e.message,'error'); }
  };

  const handleDelete = async (id) => {
    try { await schoolConfigAPI.deleteStaff(id); toast('Deleted'); load(); } catch(e) { toast(e.message,'error'); }
    setConfirmDelete(null);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await schoolConfigAPI.importStaff(fd);
      toast(`Imported ${r.data.imported} staff${r.data.created ? ', '+r.data.created+' logins' : ''}`);
      load();
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
    finally { setImporting(false); e.target.value=''; }
  };

  const openEdit = (s) => {
    setForm({
      employeeCode: s.employee_code||'', name: s.name||'', email: s.email||'',
      departmentId: s.department_id||'', designationId: s.designation_id||'',
      designation: s.designation||'', subject: s.subject||'', staffType: s.staff_type||'teacher',
      phone: s.phone||'', joinDate: s.join_date?.split('T')[0]||'',
      telegramChatId: s.telegram_chat_id||'',
      biometricUid: s.biometric_uid||'',
      gender: s.gender||'', dateOfBirth: s.date_of_birth?.split('T')[0]||'',
      reportingAuthorityId: s.reports_to_id ? String(s.reports_to_id) : '',
      createLogin: false, password:'',
      roleId: s.role_id ? String(s.role_id) : '',
      isActive: s.is_active,
      isObserver: !!s.is_observer,
      photoUrl: s.photo_url||'', signatureUrl: s.signature_url||'',
      bio: s.bio||'',
    });
    setEditing(s); setShowForm(true);
  };

  return (
    <div className="space-y-4">
      {/* ── Employees section ───────────────────────────────── */}
      <>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className={`${inp} pl-8 w-56`} placeholder="Search staff…"
              value={search} onChange={e=>setSearch(e.target.value)} />
          </div>
          <select className={inp} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
            <option value="">All Staff</option>
            <option value="teacher">Teaching</option>
            <option value="non-teaching">Non-Teaching</option>
          </select>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async()=>{const r=await schoolConfigAPI.staffTemplate();downloadBlob(r.data,'staff_template.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
            <Download size={14}/> Template
          </button>
          <label className={`flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition ${importing?'opacity-60':''}`}>
            <Upload size={14}/> {importing?'Importing…':'Import'}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </label>
          <button onClick={async()=>{const r=await schoolConfigAPI.exportStaff({staffType:typeFilter||undefined});downloadBlob(r.data,'staff_export.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
            <Download size={14}/> Export
          </button>
          <button onClick={()=>{setForm(empty);setEditing(null);setShowForm(true);}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition">
            <Plus size={14}/> Add Staff
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-medium uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Emp Id</th>
              <th className="px-4 py-3 text-left">Subject / Designation</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Class Teacher</th>
              <th className="px-4 py-3 text-left">Login / Role</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
            : filtered.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">No records. Use "Add Staff" or "Import".</td></tr>
            : filtered.map(s => (
              <tr key={s.id} className={`hover:bg-slate-50 transition ${!s.is_active?'opacity-50':''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700">
                      {s.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-700">{s.name}</p>
                      <p className="text-xs text-slate-400">{s.email||'—'}</p>
                      {s.biometric_uid && (
                        <p className="text-[10px] text-teal-600 font-mono flex items-center gap-0.5 mt-0.5">
                          <Fingerprint size={9}/> {s.biometric_uid}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.employee_code||'—'}</td>
                <td className="px-4 py-3">
                  <p className="text-slate-700">{s.subject||s.designation_name||s.designation||'—'}</p>
                  <p className="text-xs text-slate-400">{s.department_name||'—'}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.staff_type==='teacher'?'bg-emerald-50 text-emerald-700':'bg-amber-50 text-amber-700'}`}>
                    {s.staff_type==='teacher'?'Teaching':'Non-Teaching'}
                  </span>
                </td>
                {/* Class Teacher column */}
                <td className="px-4 py-3">
                  {s.class_teacher_class_id ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {s.class_teacher_grade}{s.class_teacher_section ? ` – ${s.class_teacher_section}` : ''}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
                {/* Login / Role column */}
                <td className="px-4 py-3">
                  {s.user_id ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100 capitalize w-fit">
                        <Shield size={9}/> {s.role_name || 'user'}
                      </span>
                      <span className={`text-[10px] ${s.login_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {s.login_active ? '● Active' : '○ Inactive'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">No login</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{s.phone||'—'}</td>
                <td className="px-4 py-3">
                  <button onClick={()=>handleToggle(s.id)}
                    className={`transition ${s.is_active?'text-emerald-600 hover:text-emerald-800':'text-slate-400 hover:text-slate-600'}`}>
                    {s.is_active ? <ToggleRight size={20}/> : <ToggleLeft size={20}/>}
                  </button>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={()=>openEdit(s)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={14}/></button>
                  <button onClick={()=>setConfirmDelete(s.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center px-4 pb-4 pt-20">
          <div className="bg-white w-full max-w-3xl max-h-[calc(100vh-88px)] overflow-y-auto shadow-xl rounded-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-semibold text-slate-800">{editing?'Edit Staff Member':'Add Staff Member'}</h3>
              <button onClick={()=>{setShowForm(false);setEditing(null);}} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FS label="Employee Id">
                  <input className={inp} value={form.employeeCode} onChange={e=>setForm(f=>({...f,employeeCode:e.target.value}))} placeholder="EMP001"/>
                </FS>
                <FS label="User Type">
                  <select className={inp} value={form.staffType} onChange={e=>setForm(f=>({...f,staffType:e.target.value}))}>
                    <option value="teacher">Teaching</option>
                    <option value="non-teaching">Non-Teaching</option>
                  </select>
                </FS>
              </div>
              <FS label="Full Name *">
                <input className={inp} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Full name"/>
              </FS>
              <FS label="Official Email Id">
                <input type="email" className={inp} value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="email@school.com"/>
              </FS>
              <div className="grid grid-cols-2 gap-4">
                <FS label="Department">
                  <select className={inp} value={form.departmentId} onChange={e=>setForm(f=>({...f,departmentId:e.target.value}))}>
                    <option value="">Select…</option>
                    {departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </FS>
                <FS label="Designation">
                  <select className={inp} value={form.designationId} onChange={e=>setForm(f=>({...f,designationId:e.target.value}))}>
                    <option value="">Select…</option>
                    {designations.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </FS>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FS label="Phone No.">
                  <input className={inp} value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="9876543210"/>
                </FS>
                <FS label="Joining Date">
                  <input type="date" className={inp} value={form.joinDate} onChange={e=>setForm(f=>({...f,joinDate:e.target.value}))}/>
                </FS>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FS label="Gender">
                  <select className={inp} value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))}>
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </FS>
                <FS label="Date of Birth">
                  <input type="date" className={inp} value={form.dateOfBirth} onChange={e=>setForm(f=>({...f,dateOfBirth:e.target.value}))}/>
                </FS>
              </div>
              <FS label="Reporting Authority">
                <select className={inp} value={form.reportingAuthorityId} onChange={e=>setForm(f=>({...f,reportingAuthorityId:e.target.value}))}>
                  <option value="">None</option>
                  {staff.filter(s => !editing || s.id !== editing.id).map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.designation ? ` — ${s.designation}` : ''}</option>
                  ))}
                </select>
              </FS>
              <div className="grid grid-cols-2 gap-4">
                <FS label="Telegram Chat ID">
                  <input className={inp} value={form.telegramChatId} onChange={e=>setForm(f=>({...f,telegramChatId:e.target.value}))} placeholder="e.g. 123456789"/>
                </FS>
                <FS label="Biometric Code">
                  <input className={inp} value={form.biometricUid||''} onChange={e=>setForm(f=>({...f,biometricUid:e.target.value}))} placeholder="e.g. 15"/>
                </FS>
              </div>
              <FS label="Bio">
                <textarea
                  className={`${inp} resize-y`}
                  rows={3}
                  value={form.bio||''}
                  onChange={e=>setForm(f=>({...f,bio:e.target.value}))}
                  placeholder="Short professional bio or description…"
                />
              </FS>
              {/* ── Employee Photo & Digital Signature ── */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Employee Photo',    key: 'photoUrl',     hint: 'JPG/PNG, max 2 MB' },
                  { label: 'Digital Signature', key: 'signatureUrl', hint: 'PNG preferred, white bg' },
                ].map(({ label, key, hint }) => {
                  const val = form[key];
                  return (
                    <div key={key}>
                      <p className="text-xs font-medium text-slate-600 mb-1.5">{label}</p>
                      <label className="block cursor-pointer">
                        <input type="file" accept="image/*" className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) { toast('Image must be under 2 MB', 'error'); return; }
                            const reader = new FileReader();
                            reader.onload = ev => setForm(f => ({ ...f, [key]: ev.target.result }));
                            reader.readAsDataURL(file);
                          }}
                        />
                        <div className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-colors ${val ? 'border-primary-300 bg-primary-50/30' : 'border-slate-200 bg-slate-50 hover:border-primary-300 hover:bg-primary-50/20'}`}
                          style={{ height: key === 'photoUrl' ? '110px' : '80px' }}>
                          {val ? (
                            <img src={val} alt={label}
                              className={`object-contain rounded-lg ${key === 'photoUrl' ? 'max-h-24' : 'max-h-16'}`}/>
                          ) : (
                            <>
                              <ImagePlus size={20} className="text-slate-300 mb-1"/>
                              <span className="text-[11px] text-slate-400">Click to upload</span>
                              <span className="text-[10px] text-slate-300">{hint}</span>
                            </>
                          )}
                        </div>
                      </label>
                      {val && (
                        <button type="button" onClick={() => setForm(f => ({ ...f, [key]: '' }))}
                          className="mt-1 flex items-center gap-1 text-[11px] text-red-400 hover:text-red-600 transition-colors">
                          <Trash size={11}/> Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Dashboard Login & Role section ── */}
              <div className="flex gap-2 pt-2">
                <button onClick={()=>{setShowForm(false);setEditing(null);}} className="flex-1 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
                <button onClick={save} className="flex-1 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-medium">
                  {editing?'Save Changes':'Add Staff'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog message="Delete this staff member? All attendance records will also be removed."
          onConfirm={()=>handleDelete(confirmDelete)} onCancel={()=>setConfirmDelete(null)} />
      )}
      </>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: DEPARTMENTS
// ═══════════════════════════════════════════════════════════
function DepartmentsTab({ toast }) {
  return (
    <EnhancedListManagerTab
      label="Department"
      fetchFn={schoolConfigAPI.getDepartments}
      createFn={d => schoolConfigAPI.createDepartment(d)}
      updateFn={(id, d) => schoolConfigAPI.updateDepartment(id, d)}
      deleteFn={id => schoolConfigAPI.deleteDepartment(id)}
      importFn={fd => schoolConfigAPI.importDepartments(fd)}
      exportFn={() => schoolConfigAPI.exportDepartments()}
      templateFn={() => schoolConfigAPI.departmentTemplate()}
      fields={[{ key:'name', label:'Department Name', required:true }]}
      toast={toast}
    />
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: CLASSES (Grade & Section)
// ═══════════════════════════════════════════════════════════
function ClassesTab({ toast }) {
  const [items, setItems]         = useState([]);
  const [academicYears, setAYs]   = useState([]);
  const [gradeOptions, setGrades] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editId, setEditId]       = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [importing, setImporting] = useState(false);
  const empty = { grade:'', section:'', academicYearId:'' };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ay, go] = await Promise.all([
        schoolConfigAPI.getClasses(),
        schoolConfigAPI.getAcademicYears(),
        schoolConfigAPI.getGradeOptions(),
      ]);
      setItems(c.data);
      setAYs(ay.data);
      setGrades(go.data);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId === 'new') { await schoolConfigAPI.createClass(form); toast('Class added'); }
      else { await schoolConfigAPI.updateClass(editId, form); toast('Class updated'); }
      setEditId(null); setForm(empty); load();
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await schoolConfigAPI.importClasses(fd); toast(`Imported ${r.data.imported} classes`); load(); }
    catch(e) { toast(e.response?.data?.error||e.message,'error'); }
    finally { setImporting(false); e.target.value=''; }
  };

  // Group classes by grade for display
  const grouped = items.reduce((acc, c) => {
    if (!acc[c.grade]) acc[c.grade] = [];
    acc[c.grade].push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-slate-500">{items.length} class(es) across {Object.keys(grouped).length} grade(s)</p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async()=>{const r=await schoolConfigAPI.classTemplate();downloadBlob(r.data,'classes_template.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Template
          </button>
          <label className={`flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer ${importing?'opacity-60':''}`}>
            <Upload size={14}/> {importing?'Importing…':'Import'}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </label>
          <button onClick={async()=>{const r=await schoolConfigAPI.exportClasses();downloadBlob(r.data,'classes_export.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Export
          </button>
          <button onClick={()=>{setForm(empty);setEditId('new');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={14}/> Add Class
          </button>
        </div>
      </div>

      {editId !== null && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-slate-700">{editId==='new'?'Add Grade / Section':'Edit Grade / Section'}</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Grade *</label>
              <select className={fi} value={form.grade} onChange={e=>setForm(f=>({...f,grade:e.target.value}))}>
                <option value="">Select grade…</option>
                {gradeOptions.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Section</label>
              <input className={fi} placeholder="A / B / Science" value={form.section} onChange={e=>setForm(f=>({...f,section:e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Academic Year</label>
              <select className={fi} value={form.academicYearId} onChange={e=>setForm(f=>({...f,academicYearId:e.target.value}))}>
                <option value="">Any</option>
                {academicYears.map(ay=><option key={ay.id} value={ay.id}>{ay.year_label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>{setEditId(null);setForm(empty);}} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
            <button onClick={save} className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Grade</th>
              <th className="px-4 py-3 text-left">Section</th>
              <th className="px-4 py-3 text-left">Academic Year</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? <tr><td colSpan={4} className="text-center py-6 text-slate-400">Loading…</td></tr>
            : items.length === 0 ? <tr><td colSpan={4} className="text-center py-6 text-slate-400">No classes yet. Use "Add Class" or "Import".</td></tr>
            : items.map(item => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-primary-700">{item.grade}</td>
                <td className="px-4 py-3 text-slate-600">{item.section||'—'}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{item.year_label||'—'}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={()=>{setForm({grade:item.grade,section:item.section||'',academicYearId:item.academic_year_id||''});setEditId(item.id);}}
                    className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={14}/></button>
                  <button onClick={()=>setConfirmId(item.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmId && <ConfirmDialog message="Delete this class?"
        onConfirm={async()=>{await schoolConfigAPI.deleteClass(confirmId);toast('Deleted');setConfirmId(null);load();}}
        onCancel={()=>setConfirmId(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ENHANCED LIST MANAGER (Subjects, Designations — with import/export)
// ═══════════════════════════════════════════════════════════
function EnhancedListManagerTab({
  label, fetchFn, createFn, updateFn, deleteFn,
  importFn, exportFn, templateFn,
  fields = [{ key:'name', label:'Name', required:true }],
  toast,
}) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId]   = useState(null);
  const [form, setForm]       = useState({});
  const [confirmId, setConfirmId] = useState(null);
  const [importing, setImporting] = useState(false);

  const empty = Object.fromEntries(fields.map(f=>[f.key,'']));
  const load  = useCallback(async () => {
    setLoading(true);
    try { const r = await fetchFn(); setItems(r.data); } catch {}
    finally { setLoading(false); }
  }, [fetchFn]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId === 'new') { await createFn(form); toast(`${label} added`); }
      else { await updateFn(editId, form); toast(`${label} updated`); }
      setEditId(null); setForm(empty); load();
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await importFn(fd);
      toast(`Imported ${r.data.imported} ${label.toLowerCase()}s`); load();
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
    finally { setImporting(false); e.target.value=''; }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-slate-500">{items.length} {label.toLowerCase()}(s)</p>
        <div className="flex gap-2 flex-wrap">
          {templateFn && (
            <button onClick={async()=>{const r=await templateFn();downloadBlob(r.data,`${label.toLowerCase()}_template.xlsx`);}}
              className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              <Download size={14}/> Template
            </button>
          )}
          {importFn && (
            <label className={`flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer ${importing?'opacity-60':''}`}>
              <Upload size={14}/> {importing?'Importing…':'Import'}
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
            </label>
          )}
          {exportFn && (
            <button onClick={async()=>{const r=await exportFn();downloadBlob(r.data,`${label.toLowerCase()}_export.xlsx`);}}
              className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              <Download size={14}/> Export
            </button>
          )}
          <button onClick={()=>{setForm(empty);setEditId('new');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={14}/> Add {label}
          </button>
        </div>
      </div>

      {editId !== null && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-slate-700">{editId==='new'?`Add ${label}`:`Edit ${label}`}</p>
          <div className="flex gap-2 flex-wrap">
            {fields.map(f => f.type === 'select' ? (
              <select key={f.key} className={`${inp} flex-1 min-w-[160px]`}
                value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))}>
                <option value="">{f.placeholder || `Select ${f.label}…`}</option>
                {(f.options||[]).map(o => (
                  <option key={o.value||o} value={o.value||o}>{o.label||o}</option>
                ))}
              </select>
            ) : (
              <input key={f.key} className={`${inp} flex-1 min-w-[160px]`}
                placeholder={f.label + (f.required?' *':'')}
                value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={()=>{setEditId(null);setForm(empty);}} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
            <button onClick={save} className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              {fields.map(f=><th key={f.key} className="px-4 py-3 text-left">{f.label}</th>)}
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? <tr><td colSpan={fields.length+1} className="text-center py-6 text-slate-400">Loading…</td></tr>
            : items.length === 0 ? <tr><td colSpan={fields.length+1} className="text-center py-6 text-slate-400">No {label.toLowerCase()}s yet.</td></tr>
            : items.map(item => (
              <tr key={item.id} className="hover:bg-slate-50">
                {fields.map(f=>(
                  <td key={f.key} className="px-4 py-3 text-slate-700">
                    {f.render ? f.render(item[f.key], item) : (item[f.key]||'—')}
                  </td>
                ))}
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={()=>{setForm(Object.fromEntries(fields.map(f=>[f.key,item[f.key]||''])));setEditId(item.id);}}
                    className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={14}/></button>
                  <button onClick={()=>setConfirmId(item.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmId && <ConfirmDialog message={`Delete this ${label.toLowerCase()}?`}
        onConfirm={async()=>{await deleteFn(confirmId);toast('Deleted');setConfirmId(null);load();}}
        onCancel={()=>setConfirmId(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: STUDENTS
// ═══════════════════════════════════════════════════════════
function StudentsTab({ toast }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [importing, setImporting] = useState(false);

  const empty = {
    admissionNo:'', name:'', email:'', grade:'', classId:'', section:'', rollNo:'',
    fatherName:'', fatherPhone:'', fatherEmail:'',
    motherName:'', motherPhone:'', motherEmail:'',
    dob:'', gender:'', studentType:'day_scholar', isActive:true,
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        schoolConfigAPI.getStudents({ yearId: 3 }),
        schoolConfigAPI.getClasses(),
      ]);
      setStudents(s.data);
      setClasses(c.data);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = students.filter(s => !search ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.admission_no||'').includes(search) ||
    (s.email||'').toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    try {
      if (editing) await schoolConfigAPI.updateStudent(editing.id, form);
      else         await schoolConfigAPI.createStudent({...form, yearId:3});
      toast(editing ? 'Student updated' : 'Student added');
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
  };

  const handleToggle = async (id) => {
    try { await schoolConfigAPI.toggleStudent(id); load(); } catch(e) { toast(e.message,'error'); }
  };

  const openEdit = (s) => {
    setForm({
      admissionNo: s.admission_no||'', name: s.name||'', email: s.email||'',
      grade: s.grade||'', classId: s.class_id||'', section: s.section||'', rollNo: s.roll_no||'',
      fatherName:  s.father_name||'',  fatherPhone: s.father_phone||'',  fatherEmail: s.father_email||'',
      motherName:  s.mother_name||'',  motherPhone: s.mother_phone||'',  motherEmail: s.mother_email||'',
      dob: s.dob?.split('T')[0]||'', gender: s.gender||'',
      studentType: s.student_type||'day_scholar', isActive: !!s.is_active,
    });
    setEditing(s); setShowForm(true);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await schoolConfigAPI.importStudents(fd); toast(`Imported ${r.data.imported} students`); load(); }
    catch(e) { toast(e.response?.data?.error||e.message,'error'); }
    finally { setImporting(false); e.target.value=''; }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${inp} pl-8 w-64`} placeholder="Search name, admission, email…"
            value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async()=>{const r=await schoolConfigAPI.studentTemplate();downloadBlob(r.data,'student_template.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Template
          </button>
          <label className={`flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer ${importing?'opacity-60':''}`}>
            <Upload size={14}/> {importing?'Importing…':'Import'}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </label>
          <button onClick={async()=>{const r=await schoolConfigAPI.exportStudents({yearId:3});downloadBlob(r.data,'students_export.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Export
          </button>
          <button onClick={()=>{setForm(empty);setEditing(null);setShowForm(true);}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={14}/> Add Student
          </button>
        </div>
      </div>

      <p className="text-sm text-slate-500">{filtered.length} student(s)</p>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Adm. No</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Grade</th>
              <th className="px-4 py-3 text-left">Father / Mother</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>
            : filtered.length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-400">No students yet.</td></tr>
            : filtered.map(s => (
              <tr key={s.id} className={`hover:bg-slate-50 ${!s.is_active?'opacity-50':''}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-700">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.email||'—'}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.admission_no||'—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    s.student_type==='rte'          ? 'bg-purple-50 text-purple-700' :
                    s.student_type==='day_boarding' ? 'bg-blue-50 text-blue-700' :
                                                      'bg-emerald-50 text-emerald-700'
                  }`}>
                    {s.student_type==='rte' ? 'RTE' : s.student_type==='day_boarding' ? 'Day Boarding' : 'Day Scholar'}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">
                  {s.grade ? `${s.grade}-${s.section||s.class_section||''}` : '—'}
                </td>
                <td className="px-4 py-3 text-xs">
                  {(s.father_name||s.father_phone) && (
                    <p className="text-slate-600">
                      <span className="font-medium text-slate-400">F: </span>
                      {s.father_name||'—'} {s.father_phone ? `· ${s.father_phone}` : ''}
                    </p>
                  )}
                  {(s.mother_name||s.mother_phone) && (
                    <p className="text-slate-500 mt-0.5">
                      <span className="font-medium text-slate-400">M: </span>
                      {s.mother_name||'—'} {s.mother_phone ? `· ${s.mother_phone}` : ''}
                    </p>
                  )}
                  {!s.father_name && !s.mother_name && <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={()=>handleToggle(s.id)}
                    className={`transition ${s.is_active?'text-emerald-600 hover:text-emerald-800':'text-slate-400 hover:text-slate-600'}`}>
                    {s.is_active ? <ToggleRight size={20}/> : <ToggleLeft size={20}/>}
                  </button>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={()=>openEdit(s)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={14}/></button>
                  <button onClick={()=>setConfirmId(s.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end">
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{editing?'Edit Student':'Add Student'}</h3>
              <button onClick={()=>{setShowForm(false);setEditing(null);}} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[['admissionNo','Admission No'],['name','Student Name *'],['email','School Email Id'],['rollNo','Roll No']].map(([k,l])=>(
                <FS key={k} label={l}>
                  <input type={k==='email'?'email':'text'} className={fi} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
                </FS>
              ))}
              <FS label="Grade">
                <select className={fi} value={form.grade}
                  onChange={e => setForm(f => ({ ...f, grade: e.target.value, section: '', classId: '' }))}>
                  <option value="">Select grade…</option>
                  {[...new Set(classes.map(c => c.grade))].sort().map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </FS>
              <FS label="Section">
                <select className={fi} value={form.section}
                  onChange={e => {
                    const sec = e.target.value;
                    const cls = classes.find(c => c.grade === form.grade && (c.section||'') === sec);
                    setForm(f => ({ ...f, section: sec, classId: cls?.id || '' }));
                  }}>
                  <option value="">Select section…</option>
                  {classes.filter(c => c.grade === form.grade).map(c => (
                    <option key={c.id} value={c.section||''}>{c.section || '(no section)'}</option>
                  ))}
                </select>
              </FS>
              <FS label="Student Type">
                <select className={fi} value={form.studentType} onChange={e=>setForm(f=>({...f,studentType:e.target.value}))}>
                  <option value="day_scholar">Day Scholar</option>
                  <option value="day_boarding">Day Boarding</option>
                  <option value="rte">RTE</option>
                </select>
              </FS>
              <FS label="Gender">
                <select className={fi} value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))}>
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </FS>
              <FS label="Date of Birth">
                <input type="date" className={fi} value={form.dob} onChange={e=>setForm(f=>({...f,dob:e.target.value}))} />
              </FS>
              {/* Father's details */}
              <div className="col-span-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[9px] font-bold flex items-center justify-center">F</span>
                  Father's Details
                </p>
              </div>
              {[['fatherName','Father Name'],['fatherPhone','Father Phone No.'],['fatherEmail','Father Email Id']].map(([k,l])=>(
                <FS key={k} label={l}>
                  <input className={fi} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={k==='fatherEmail'?'father@email.com':k==='fatherPhone'?'9876543210':''} />
                </FS>
              ))}

              {/* Mother's details */}
              <div className="col-span-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-pink-100 text-pink-600 text-[9px] font-bold flex items-center justify-center">M</span>
                  Mother's Details
                </p>
              </div>
              {[['motherName','Mother Name'],['motherPhone','Mother Phone No.'],['motherEmail','Mother Email Id']].map(([k,l])=>(
                <FS key={k} label={l}>
                  <input className={fi} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={k==='motherEmail'?'mother@email.com':k==='motherPhone'?'9876543210':''} />
                </FS>
              ))}
              <div className="col-span-2 flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                  <input type="checkbox" checked={form.isActive} onChange={e=>setForm(f=>({...f,isActive:e.target.checked}))} className="rounded" />
                  Active
                </label>
              </div>
              <div className="col-span-2 flex gap-2 pt-2">
                <button onClick={()=>{setShowForm(false);setEditing(null);}} className="flex-1 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
                <button onClick={save} className="flex-1 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-medium">
                  {editing?'Save Changes':'Add Student'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmId && <ConfirmDialog message="Delete this student?" onConfirm={async()=>{await schoolConfigAPI.deleteStudent(confirmId);toast('Deleted');setConfirmId(null);load();}} onCancel={()=>setConfirmId(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: ACADEMIC — School info + Logo + Academic Years
// ═══════════════════════════════════════════════════════════
function AcademicTab({ toast }) {
  const [config, setConfig]         = useState({});
  const [academicYears, setAYears]  = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [logoPreview, setPreview]   = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const [showAYForm, setShowAYForm] = useState(false);
  const [ayForm, setAyForm]         = useState({ yearLabel:'', startDate:'', endDate:'', isCurrent:false });
  const [savingAY, setSavingAY]     = useState(false);
  const fileRef                     = useRef(null);
  const BASE_URL = 'http://localhost:5000';

  useEffect(() => {
    Promise.all([schoolConfigAPI.getConfig(), schoolConfigAPI.getAcademicYears()])
      .then(([cfg, ay]) => { setConfig(cfg.data); setAYears(ay.data); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const savedLogo  = config.school_logo ? `${BASE_URL}${config.school_logo}` : null;
  const displayLogo = logoPreview || savedLogo;

  const processFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast('Please select an image file', 'error'); return; }
    if (file.size > 2*1024*1024) { toast('Logo must be under 2 MB', 'error'); return; }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData(); fd.append('logo', file);
    try {
      const r = await schoolConfigAPI.uploadLogo(fd);
      setConfig(c=>({...c, school_logo: r.data.logoUrl}));
      toast('School logo updated');
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); setPreview(null); }
    finally { setUploading(false); }
  };

  const saveInfo = async () => {
    setSaving(true);
    const updates = {};
    ['school_name','school_address','school_phone','school_email'].forEach(k=>{
      updates[k] = document.getElementById(`sc_${k}`)?.value || '';
    });
    try { await schoolConfigAPI.saveConfig({ updates }); toast('School info saved'); }
    catch(e) { toast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const saveAcademicYear = async () => {
    setSavingAY(true);
    try {
      await schoolConfigAPI.createAcademicYear(ayForm);
      toast('Academic year added');
      setShowAYForm(false); setAyForm({yearLabel:'',startDate:'',endDate:'',isCurrent:false});
      const ay = await schoolConfigAPI.getAcademicYears(); setAYears(ay.data);
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
    finally { setSavingAY(false); }
  };

  const setActiveYear = async (id) => {
    try {
      await schoolConfigAPI.setActiveAcademicYear(id);
      const ay = await schoolConfigAPI.getAcademicYears(); setAYears(ay.data);
      toast('Active year updated');
    } catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <div className="text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-5">
      {/* Logo */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-700">School Logo</p>
            <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, SVG or WebP · Max 2 MB</p>
          </div>
          {displayLogo && (
            <button onClick={async()=>{await schoolConfigAPI.deleteLogo();setPreview(null);setConfig(c=>({...c,school_logo:''}));toast('Logo removed');}}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
              <Trash size={13}/> Remove
            </button>
          )}
        </div>
        <div className="flex items-start gap-5">
          <div className={`relative w-32 h-32 rounded-2xl border-2 flex items-center justify-center overflow-hidden flex-shrink-0 transition
            ${dragOver?'border-primary-500 bg-primary-50':'border-dashed border-slate-300 bg-slate-50'}`}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{e.preventDefault();setDragOver(false);processFile(e.dataTransfer.files[0]);}}>
            {displayLogo ? <img src={displayLogo} alt="Logo" className="w-full h-full object-contain p-2" onError={()=>setPreview(null)} />
                         : <div className="text-center text-slate-400"><ImagePlus size={28} className="mx-auto mb-1"/><p className="text-xs">Drop here</p></div>}
            {uploading && <div className="absolute inset-0 bg-white/80 flex items-center justify-center"><RefreshCw size={20} className="text-primary-600 animate-spin"/></div>}
          </div>
          <div className="flex-1 space-y-3">
            <p className="text-sm text-slate-600">{displayLogo?'Logo set. Click to replace.':'No logo. Upload your school logo.'}</p>
            <button onClick={()=>fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-60 w-full justify-center">
              <Upload size={15}/> {uploading?'Uploading…':displayLogo?'Replace Logo':'Upload Logo'}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={e=>processFile(e.target.files[0])} />
          </div>
        </div>
      </div>

      {/* Login Page Design */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div>
          <p className="text-sm font-semibold text-slate-700">Login Page Design</p>
          <p className="text-xs text-slate-400 mt-0.5">Choose how the sign-in screen looks for everyone</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'premium_dark',     name: 'Premium Dark',     desc: 'Dark screen, gold accent (default)',
              preview: <div className="h-16 rounded-lg bg-[#0c2233] flex flex-col items-center justify-center gap-1">
                         <div className="w-4 h-4 rounded-full bg-white" />
                         <div className="w-6 h-0.5 bg-[#c9a961]" />
                       </div> },
            { key: 'enterprise_split', name: 'Enterprise Split', desc: 'Brand panel left, white form right',
              preview: <div className="h-16 rounded-lg overflow-hidden flex border border-slate-200">
                         <div className="flex-[1.3] bg-primary-800" />
                         <div className="flex-1 bg-white flex flex-col items-center justify-center gap-1">
                           <div className="w-8 h-1 bg-slate-200 rounded" />
                           <div className="w-8 h-1 bg-slate-200 rounded" />
                           <div className="w-8 h-1.5 bg-primary-600 rounded" />
                         </div>
                       </div> },
            { key: 'centered_minimal', name: 'Centered Minimal', desc: 'Single card on light background',
              preview: <div className="h-16 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200">
                         <div className="w-12 h-11 bg-white border border-slate-200 rounded flex flex-col items-center justify-center gap-1">
                           <div className="w-3 h-3 rounded-full bg-slate-300 -mt-2" />
                           <div className="w-7 h-1 bg-slate-200 rounded" />
                           <div className="w-7 h-1 bg-primary-600 rounded" />
                         </div>
                       </div> },
          ].map(d => {
            const selected = (config.login_design || 'premium_dark') === d.key;
            return (
              <button
                key={d.key}
                onClick={async () => {
                  try {
                    await schoolConfigAPI.saveConfig({ updates: { login_design: d.key } });
                    setConfig(c => ({ ...c, login_design: d.key }));
                    sessionStorage.removeItem('login_branding');
                    toast('Login design updated');
                  } catch(e) { toast(e.response?.data?.error || e.message, 'error'); }
                }}
                className={`text-left rounded-xl border-2 p-3 space-y-2 transition
                  ${selected ? 'border-primary-500 bg-primary-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
              >
                {d.preview}
                <div>
                  <p className={`text-xs font-semibold ${selected ? 'text-primary-700' : 'text-slate-700'}`}>{d.name}</p>
                  <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{d.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Academic Years */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-700">Academic Years</p>
          <button onClick={()=>setShowAYForm(v=>!v)}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={13}/> Add Year
          </button>
        </div>
        {showAYForm && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex flex-col gap-1">
                <label className="text-xs text-slate-500 uppercase tracking-wide">Year Label *</label>
                <input className={fi} placeholder="e.g. 2027-28" value={ayForm.yearLabel} onChange={e=>setAyForm(f=>({...f,yearLabel:e.target.value}))} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500 uppercase tracking-wide">Start Date *</label>
                <input type="date" className={fi} value={ayForm.startDate} onChange={e=>setAyForm(f=>({...f,startDate:e.target.value}))} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500 uppercase tracking-wide">End Date</label>
                <input type="date" className={fi} value={ayForm.endDate} onChange={e=>setAyForm(f=>({...f,endDate:e.target.value}))} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={ayForm.isCurrent} onChange={e=>setAyForm(f=>({...f,isCurrent:e.target.checked}))} className="rounded"/>
              Set as active year
            </label>
            <div className="flex gap-2">
              <button onClick={()=>setShowAYForm(false)} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
              <button onClick={saveAcademicYear} disabled={savingAY} className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60">
                {savingAY?'Saving…':'Save Year'}
              </button>
            </div>
          </div>
        )}
        <div className="space-y-2">
          {academicYears.map(ay => (
            <div key={ay.id} className={`flex items-center justify-between px-4 py-2.5 rounded-xl border transition
              ${ay.is_current?'border-primary-300 bg-primary-50':'border-slate-200 bg-white'}`}>
              <div>
                <span className="text-sm font-medium text-slate-700">{ay.year_label}</span>
                <span className="text-xs text-slate-400 ml-2">
                  {ay.start_date ? new Date(ay.start_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : ''} –{' '}
                  {ay.end_date   ? new Date(ay.end_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : 'ongoing'}
                </span>
              </div>
              {ay.is_current
                ? <span className="text-xs font-semibold px-2 py-0.5 bg-primary-600 text-white rounded-full">Active</span>
                : <button onClick={()=>setActiveYear(ay.id)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">Set Active</button>}
            </div>
          ))}
          {!academicYears.length && <p className="text-sm text-slate-400 text-center py-2">No academic years configured.</p>}
        </div>
      </div>

      {/* School Info */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <p className="text-sm font-semibold text-slate-700">School Information</p>
        {[['school_name','School Name'],['school_address','Address'],['school_phone','Phone'],['school_email','Email']].map(([k,l])=>(
          <div key={k} className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">{l}</label>
            <input className={fi} defaultValue={config[k]||''} id={`sc_${k}`} />
          </div>
        ))}
        <button onClick={saveInfo} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-60">
          <Save size={14}/> {saving?'Saving…':'Save School Info'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: TELEGRAM — Multiple configurable notification schedules
// ═══════════════════════════════════════════════════════════
const DEFAULT_SCHEDULES = [
  { id:1, name:'Individual Alert',  event:'individual_alert',  time:'09:30', days:['Mon','Tue','Wed','Thu','Fri','Sat'], enabled:true },
  { id:2, name:'Management Report', event:'management_report', time:'18:00', days:['Mon','Tue','Wed','Thu','Fri','Sat'], enabled:true },
  { id:3, name:'Monthly Summary',   event:'monthly_report',    time:'08:00', days:[], dayOfMonth:1, enabled:true },
];
const EVENT_TYPES = [
  { value:'individual_alert',  label:'Individual Alert (absent/late to each staff)' },
  { value:'management_report', label:'Management Report (daily summary)' },
  { value:'monthly_report',    label:'Monthly Summary Report' },
  { value:'weekly_overview',   label:'Weekly Overview' },
  { value:'daily_overview',    label:'Daily Overview (legacy)' },
];
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function TelegramTab({ toast }) {
  const [cfg, setCfg]           = useState({});
  const [schedules, setSchedules] = useState(DEFAULT_SCHEDULES);
  const [showToken, setShow]    = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editSch, setEditSch]   = useState(null);
  const [schForm, setSchForm]   = useState({});

  useEffect(()=>{
    schoolConfigAPI.getConfig().then(r=>{
      setCfg(r.data);
      if (r.data.notification_schedules && Array.isArray(r.data.notification_schedules) && r.data.notification_schedules.length) {
        setSchedules(r.data.notification_schedules);
      }
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const save = async () => {
    setSaving(true);
    try {
      await schoolConfigAPI.saveConfig({ updates: {
        telegram_bot_token:    cfg.telegram_bot_token||'',
        telegram_mgmt_chat_id: cfg.telegram_mgmt_chat_id||'',
        late_threshold:        cfg.late_threshold||'09:15',
        notification_schedules: schedules,
      }});
      toast('Telegram configuration saved · Restart server to apply schedule changes');
    } catch(e) { toast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const addSchedule = () => {
    const newId = Date.now();
    setSchForm({ id:newId, name:'New Schedule', event:'individual_alert', time:'09:00', days:['Mon','Tue','Wed','Thu','Fri','Sat'], enabled:true });
    setEditSch('new');
  };

  const saveSchedule = () => {
    if (editSch === 'new') {
      setSchedules(s => [...s, schForm]);
    } else {
      setSchedules(s => s.map(x => x.id === editSch ? schForm : x));
    }
    setEditSch(null);
  };

  const toggleDay = (day) => {
    setSchForm(f => {
      const days = f.days||[];
      return { ...f, days: days.includes(day) ? days.filter(d=>d!==day) : [...days, day] };
    });
  };

  if (loading) return <div className="text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <p className="font-semibold mb-1">Setup Guide</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Create a bot with <strong>@BotFather</strong> on Telegram → get Bot Token</li>
          <li>Add bot to your management group / get Chat ID via <strong>@userinfobot</strong></li>
          <li>Set each staff member's Telegram Chat ID in the <strong>Employee Info</strong> tab</li>
          <li>Schedule changes take effect after <strong>server restart</strong></li>
        </ol>
      </div>

      {/* Bot config */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <p className="font-semibold text-slate-700 text-sm">Bot Configuration</p>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">Bot Token</label>
          <div className="relative">
            <input type={showToken?'text':'password'} className={fi} value={cfg.telegram_bot_token||''}
              onChange={e=>setCfg(c=>({...c,telegram_bot_token:e.target.value}))} placeholder="123456789:AAB…" />
            <button onClick={()=>setShow(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {showToken?<EyeOff size={15}/>:<Eye size={15}/>}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">Management Chat ID</label>
          <input className={fi} value={cfg.telegram_mgmt_chat_id||''}
            onChange={e=>setCfg(c=>({...c,telegram_mgmt_chat_id:e.target.value}))} placeholder="-100XXXXXXXXX or 123456789"/>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">Late Threshold</label>
          <input type="time" className={`${inp}`} value={cfg.late_threshold||'09:15'} onChange={e=>setCfg(c=>({...c,late_threshold:e.target.value}))} />
          <p className="text-xs text-slate-400">In-time after this → marked Late</p>
        </div>
      </div>

      {/* Notification Schedules */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-semibold text-slate-700 text-sm">Notification Schedules</p>
          <button onClick={addSchedule}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={13}/> Add Schedule
          </button>
        </div>

        {/* Edit/Add form */}
        {editSch !== null && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              {editSch==='new'?'Add Schedule':'Edit Schedule'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex flex-col gap-1">
                <label className="text-xs text-slate-500 uppercase">Schedule Name</label>
                <input className={fi} value={schForm.name||''} onChange={e=>setSchForm(f=>({...f,name:e.target.value}))} />
              </div>
              <div className="col-span-2 flex flex-col gap-1">
                <label className="text-xs text-slate-500 uppercase">Event Type</label>
                <select className={fi} value={schForm.event||'individual_alert'} onChange={e=>setSchForm(f=>({...f,event:e.target.value}))}>
                  {EVENT_TYPES.map(et=><option key={et.value} value={et.value}>{et.label}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500 uppercase">Time</label>
                <input type="time" className={fi} value={schForm.time||'09:00'} onChange={e=>setSchForm(f=>({...f,time:e.target.value}))} />
              </div>
              {schForm.event === 'monthly_report' ? (
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-500 uppercase">Day of Month</label>
                  <select className={fi} value={schForm.dayOfMonth||1} onChange={e=>setSchForm(f=>({...f,dayOfMonth:parseInt(e.target.value)}))}>
                    {[1,2,3,4,5].map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-500 uppercase">Days</label>
                  <div className="flex gap-1 flex-wrap">
                    {DAYS.map(d=>(
                      <button key={d} type="button"
                        onClick={()=>toggleDay(d)}
                        className={`text-xs px-2 py-1 rounded-md border transition font-medium
                          ${(schForm.days||[]).includes(d)?'bg-primary-600 text-white border-primary-600':'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={schForm.enabled!==false} onChange={e=>setSchForm(f=>({...f,enabled:e.target.checked}))} className="rounded"/>
              Enabled
            </label>
            <div className="flex gap-2">
              <button onClick={()=>setEditSch(null)} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
              <button onClick={saveSchedule} className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
            </div>
          </div>
        )}

        {/* Schedule list */}
        <div className="space-y-2">
          {schedules.map(sch => (
            <div key={sch.id} className={`flex items-center justify-between px-4 py-3 rounded-xl border transition
              ${sch.enabled?'border-slate-200 bg-white':'border-slate-100 bg-slate-50 opacity-60'}`}>
              <div>
                <p className="text-sm font-medium text-slate-700">{sch.name}</p>
                <p className="text-xs text-slate-400">
                  {EVENT_TYPES.find(e=>e.value===sch.event)?.label || sch.event}
                  {' · '}{sch.time}
                  {sch.event==='monthly_report' ? ` · Day ${sch.dayOfMonth||1} of month` : ` · ${(sch.days||[]).join(', ')}`}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>{setSchForm({...sch});setEditSch(sch.id);}}
                  className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={13}/></button>
                <button onClick={()=>setSchedules(s=>s.filter(x=>x.id!==sch.id))}
                  className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
          {!schedules.length && <p className="text-sm text-slate-400 text-center py-2">No schedules configured.</p>}
        </div>
      </div>

      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-60">
        <Save size={15}/> {saving?'Saving…':'Save Telegram Config'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: EMAIL
// ═══════════════════════════════════════════════════════════
function EmailTab({ toast }) {
  const [cfg, setCfg]       = useState({});
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(()=>{
    schoolConfigAPI.getConfig().then(r=>setCfg(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const save = async()=>{
    setSaving(true);
    try {
      await schoolConfigAPI.saveConfig({ updates: {
        email_host: cfg.email_host||'', email_port: cfg.email_port||'587',
        email_user: cfg.email_user||'', email_password: cfg.email_password||'',
        email_from: cfg.email_from||'', email_notify_enabled: cfg.email_notify_enabled||false,
      }});
      toast('Email configuration saved');
    } catch(e) { toast(e.message,'error'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="max-w-xl space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <p className="font-semibold text-slate-700 text-sm">SMTP Configuration</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1 col-span-2">
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">SMTP Host</label>
            <input className={fi} value={cfg.email_host||''} onChange={e=>setCfg(c=>({...c,email_host:e.target.value}))} placeholder="smtp.gmail.com"/>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">Port</label>
            <input className={fi} value={cfg.email_port||'587'} onChange={e=>setCfg(c=>({...c,email_port:e.target.value}))} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">From Address</label>
            <input className={fi} value={cfg.email_from||''} onChange={e=>setCfg(c=>({...c,email_from:e.target.value}))} placeholder="noreply@school.com"/>
          </div>
          <div className="flex flex-col gap-1 col-span-2">
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">Username / Email</label>
            <input className={fi} value={cfg.email_user||''} onChange={e=>setCfg(c=>({...c,email_user:e.target.value}))} />
          </div>
          <div className="flex flex-col gap-1 col-span-2">
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wide">Password</label>
            <div className="relative">
              <input type={showPw?'text':'password'} className={fi} value={cfg.email_password||''} onChange={e=>setCfg(c=>({...c,email_password:e.target.value}))} />
              <button onClick={()=>setShowPw(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPw?<EyeOff size={15}/>:<Eye size={15}/>}
              </button>
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!cfg.email_notify_enabled} onChange={e=>setCfg(c=>({...c,email_notify_enabled:e.target.checked}))} className="rounded"/>
          <span className="text-slate-700">Enable email notifications</span>
        </label>
      </div>
      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-60">
        <Save size={15}/> {saving?'Saving…':'Save Email Config'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: HOLIDAYS / SCHOOL ACTIVITY CALENDAR
// ═══════════════════════════════════════════════════════════
const ACTIVITY_CATEGORIES = [
  'FA (Formative Assessment)', 'SA (Summative Assessment)', 'FA & SA',
  'Assessment / Exam', 'Holiday', 'Field Trip', 'Guest Speaker',
  'Parent Connect / PTC', 'Inter-House / Inter-School', 'Assembly',
  'Student Empowerment Program', 'Teacher Well-Being', 'PBL',
  'INSET / Training', 'Unit Begin / End', 'Report Card', 'Annual Events',
  'International / National Day', 'Break / Vacation', 'Sports Day',
  'Sports Competition', 'Celebration / Cultural Event', 'Orientation',
  'School Reopening', 'General Activity',
];

const CATEGORY_STYLE = {
  'FA (Formative Assessment)':    { bg:'#C6EFCE', color:'#375623' },
  'SA (Summative Assessment)':    { bg:'#FCE4D6', color:'#843C0C' },
  'FA & SA':                      { bg:'#FFEB9C', color:'#7D5E00' },
  'Assessment / Exam':            { bg:'#FFC7CE', color:'#9C0006' },
  'Holiday':                      { bg:'#D9D9D9', color:'#595959' },
  'Field Trip':                   { bg:'#DDEBF7', color:'#1F497D' },
  'Guest Speaker':                { bg:'#EBF3FB', color:'#1F497D' },
  'Parent Connect / PTC':         { bg:'#F4CCFF', color:'#6600CC' },
  'Inter-House / Inter-School':   { bg:'#FFE699', color:'#7F6000' },
  'Assembly':                     { bg:'#E2EFDA', color:'#375623' },
  'Student Empowerment Program':  { bg:'#FFF2CC', color:'#7F6000' },
  'Teacher Well-Being':           { bg:'#FCE4D6', color:'#843C0C' },
  'PBL':                          { bg:'#D9EAD3', color:'#274E13' },
  'INSET / Training':             { bg:'#CFE2F3', color:'#1F497D' },
  'Unit Begin / End':             { bg:'#EAD1DC', color:'#4A235A' },
  'Report Card':                  { bg:'#D9D2E9', color:'#20124D' },
  'Annual Events':                { bg:'#FFE599', color:'#7F6000' },
  'International / National Day': { bg:'#D0E4FF', color:'#1F497D' },
  'Break / Vacation':             { bg:'#EFEFEF', color:'#595959' },
  'Sports Day':                   { bg:'#B6D7A8', color:'#274E13' },
  'Sports Competition':           { bg:'#B6D7A8', color:'#274E13' },
  'Celebration / Cultural Event': { bg:'#FFD966', color:'#7F6000' },
  'Orientation':                  { bg:'#A4C2F4', color:'#1F497D' },
  'School Reopening':             { bg:'#B4A7D6', color:'#20124D' },
  'General Activity':             { bg:'#E8ECF0', color:'#475569' },
};

const MONTHS_ORDER = ['April','May','June','July','August','September','October','November','December','January','February','March'];

function CategoryBadge({ category, type }) {
  const label = category || type || 'event';
  const s = CATEGORY_STYLE[label];
  if (s) return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.color }}>{label}</span>
  );
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{label}</span>;
}

function HolidaysTab({ toast }) {
  const [items, setItems]     = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [editId, setEditId]   = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const emptyForm = {
    title:'', category:'Holiday', description:'', fromDate:'', toDate:'', grade:'',
    calendarFor:'all', isHoliday:true, isStaffHoliday:true, isRecurring:false,
    teacherName:'', contactNumber:'', socialMediaHandle:'', organization:'',
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const [h, c] = await Promise.all([
        schoolConfigAPI.getHolidays({ yearId:3 }),
        schoolConfigAPI.getClasses(),
      ]);
      setItems(h.data);
      setClasses(c.data);
    } catch {}
    finally { setLoading(false); }
  },[]);
  useEffect(()=>{ load(); },[load]);

  const save = async()=>{
    try {
      const payload = { ...form, yearId:3 };
      if (editId === 'new') { await schoolConfigAPI.createHoliday(payload); toast('Added'); }
      else { await schoolConfigAPI.updateHoliday(editId, payload); toast('Updated'); }
      setEditId(null); setForm(emptyForm); load();
    } catch(e) { toast(e.response?.data?.error||e.message,'error'); }
  };

  const openEdit = (h) => {
    setForm({
      title:             h.title||'',
      category:          h.category||'Holiday',
      description:       h.description||'',
      fromDate:          (h.from_date||h.date||'').split('T')[0],
      toDate:            (h.to_date||h.from_date||h.date||'').split('T')[0],
      grade:             h.grade||'',
      calendarFor:       h.calendar_for||'all',
      isHoliday:         h.is_holiday!==undefined ? !!h.is_holiday : true,
      isStaffHoliday:    h.is_staff_holiday!==undefined ? !!h.is_staff_holiday : true,
      isRecurring:       !!h.is_recurring,
      teacherName:       h.teacher_name||'',
      contactNumber:     h.contact_number||'',
      socialMediaHandle: h.social_media_handle||'',
      organization:      h.organization||'',
    });
    setEditId(h.id);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await schoolConfigAPI.importHolidays(fd, { yearId:3 }); toast(`Imported ${r.data.imported} entries`); load(); }
    catch(e) { toast(e.response?.data?.error||e.message,'error'); }
    finally { setImporting(false); e.target.value=''; }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : '—';
  const getMonth = (h) => {
    const d = h.from_date || h.date;
    if (!d) return '';
    return new Date(d).toLocaleString('en-US',{month:'long'});
  };

  const filtered = items.filter(h => {
    if (filterMonth && getMonth(h) !== filterMonth) return false;
    if (filterCat) {
      const match = h.category === filterCat ||
        (!h.category && h.type?.toLowerCase() === filterCat.toLowerCase());
      if (!match) return false;
    }
    return true;
  });

  const grouped = {};
  filtered.forEach(h => {
    const m = getMonth(h) || 'Unknown';
    if (!grouped[m]) grouped[m] = [];
    grouped[m].push(h);
  });
  const orderedMonths = MONTHS_ORDER.filter(m => grouped[m]).concat(
    Object.keys(grouped).filter(m => !MONTHS_ORDER.includes(m))
  );

  const Chk = ({ label, field }) => (
    <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer whitespace-nowrap">
      <input type="checkbox" className="rounded" checked={!!form[field]} onChange={e=>setForm(f=>({...f,[field]:e.target.checked}))} />
      {label}
    </label>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-slate-500">{filtered.length} / {items.length} entries · AY 2026-27</p>
          <select className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600"
            value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
            <option value="">All Months</option>
            {MONTHS_ORDER.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <select className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600"
            value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
            <option value="">All Categories</option>
            {ACTIVITY_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async()=>{const r=await schoolConfigAPI.holidayTemplate();downloadBlob(r.data,'school_calendar_template.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Template
          </button>
          <label className={`flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer ${importing?'opacity-60':''}`}>
            <Upload size={14}/> {importing?'Importing…':'Import'}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </label>
          <button onClick={async()=>{const r=await schoolConfigAPI.exportHolidays({yearId:3});downloadBlob(r.data,'school_calendar_export.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Export
          </button>
          <button onClick={()=>{setForm(emptyForm);setEditId('new');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={14}/> Add
          </button>
        </div>
      </div>

      {/* Add / Edit Form */}
      {editId !== null && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-slate-700">{editId==='new'?'Add Calendar Entry':'Edit Entry'}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Title / Activity *</label>
              <input className={`${fi} w-full`} placeholder="e.g. Diwali / Republic Day Celebration" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} />
            </div>
            <div className="col-span-2 flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Category *</label>
              <select className={`${fi} w-full`} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {ACTIVITY_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              {form.category && CATEGORY_STYLE[form.category] && (
                <div className="mt-1"><CategoryBadge category={form.category} /></div>
              )}
            </div>
            <div className="col-span-2 flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Description / Details</label>
              <textarea className={`${fi} w-full resize-none`} rows={2} placeholder="Activity details, notes…"
                value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">From Date *</label>
              <input type="date" className={`${fi} w-full`} value={form.fromDate}
                onChange={e=>setForm(f=>({...f,fromDate:e.target.value, toDate:f.toDate||e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">To Date</label>
              <input type="date" className={`${fi} w-full`} value={form.toDate}
                onChange={e=>setForm(f=>({...f,toDate:e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Grade / Class</label>
              <select className={`${fi} w-full`} value={form.grade} onChange={e=>setForm(f=>({...f,grade:e.target.value}))}>
                <option value="">All Grades</option>
                {[...new Set(classes.map(c=>c.grade))].sort().map(g=>(
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Calendar For</label>
              <select className={`${fi} w-full`} value={form.calendarFor} onChange={e=>setForm(f=>({...f,calendarFor:e.target.value}))}>
                <option value="all">All (Staff + Students)</option>
                <option value="staff">Staff Only</option>
                <option value="students">Students Only</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Teacher / In-charge</label>
              <input className={`${fi} w-full`} placeholder="Teacher name" value={form.teacherName}
                onChange={e=>setForm(f=>({...f,teacherName:e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Contact Number</label>
              <input className={`${fi} w-full`} placeholder="Phone / extension" value={form.contactNumber}
                onChange={e=>setForm(f=>({...f,contactNumber:e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Organization</label>
              <input className={`${fi} w-full`} placeholder="Organizing body / partner" value={form.organization}
                onChange={e=>setForm(f=>({...f,organization:e.target.value}))} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Social Media Handle</label>
              <input className={`${fi} w-full`} placeholder="@handle or URL" value={form.socialMediaHandle}
                onChange={e=>setForm(f=>({...f,socialMediaHandle:e.target.value}))} />
            </div>
            <div className="col-span-2 flex flex-wrap gap-4 pt-1">
              <Chk label="Is Holiday (no classes)" field="isHoliday" />
              <Chk label="Is Staff Holiday"        field="isStaffHoliday" />
              <Chk label="Recurring yearly"        field="isRecurring" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>setEditId(null)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
            <button onClick={save} className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
          </div>
        </div>
      )}

      {/* Calendar Table — grouped by month */}
      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-slate-400">No entries found.</div>
      ) : (
        <div className="space-y-3">
          {orderedMonths.map(month => (
            <div key={month} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">{month}</span>
                <span className="text-xs text-slate-400">{grouped[month].length} entries</span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left w-28">Date</th>
                    <th className="px-4 py-2 text-left">Title</th>
                    <th className="px-4 py-2 text-left">Category</th>
                    <th className="px-4 py-2 text-left hidden md:table-cell">Grade</th>
                    <th className="px-4 py-2 text-left hidden lg:table-cell">Teacher</th>
                    <th className="px-4 py-2 text-left hidden lg:table-cell">Holiday</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {grouped[month].map(h => {
                    const fd = h.from_date||h.date;
                    const td = h.to_date||h.from_date||h.date;
                    const sameDay = fd?.split('T')[0] === td?.split('T')[0];
                    return (
                      <tr key={h.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-slate-600 text-xs whitespace-nowrap">
                          {fmtDate(fd)}{!sameDay ? <><br/><span className="text-slate-400">→ {fmtDate(td)}</span></> : ''}
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-slate-700 text-sm">{h.title}</p>
                          {h.description && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{h.description}</p>}
                        </td>
                        <td className="px-4 py-2.5">
                          <CategoryBadge category={h.category} type={h.type} />
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs hidden md:table-cell">{h.grade||'All'}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs hidden lg:table-cell">{h.teacher_name||'—'}</td>
                        <td className="px-4 py-2.5 text-xs hidden lg:table-cell">
                          {h.is_holiday ? <span className="text-emerald-600 font-medium">Yes</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex gap-1 justify-end">
                            <button onClick={()=>openEdit(h)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={14}/></button>
                            <button onClick={()=>setConfirmId(h.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Category Legend</p>
        <div className="flex flex-wrap gap-2">
          {ACTIVITY_CATEGORIES.map(cat => <CategoryBadge key={cat} category={cat} />)}
        </div>
      </div>

      {confirmId && <ConfirmDialog message="Delete this calendar entry?" onConfirm={async()=>{await schoolConfigAPI.deleteHoliday(confirmId);toast('Deleted');setConfirmId(null);load();}} onCancel={()=>setConfirmId(null)} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: USERS & CREDENTIALS
// ══════════════════════════════════════════════════════════
function UsersCredentialsTab({ toast }) {
  const [users,   setUsers]   = useState([]);
  const [roles,   setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [confirmId,setConfirmId]= useState(null);
  const [form, setForm] = useState({ name:'', email:'', role:'', password:'' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([authAPI.getUsers(), rolesAPI.getAll()]);
      setUsers(u.data);
      setRoles(r.data);
    } catch(e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const openAdd = () => {
    setForm({ name:'', email:'', role:'user', password:'Admin@123' });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setForm({ name:u.name, email:u.email, role:u.role });
    setEditing(u);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast('Name is required','error'); return; }
    if (!editing && !form.email.trim()) { toast('Email is required','error'); return; }
    setSaving(true);
    try {
      if (editing) {
        await authAPI.updateUser(editing.id, {
          name: form.name,
          email: form.email,
          role: form.role,
        });
        toast('User updated');
      } else {
        await authAPI.createUser({ name:form.name, email:form.email, role:form.role, password:form.password });
        toast('User created · Password: ' + (form.password || 'Admin@123'));
      }
      setShowForm(false); setEditing(null); load();
    } catch(e) { toast(e.response?.data?.error || e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await authAPI.deleteUser(id); toast('User deleted'); load(); }
    catch(e) { toast(e.response?.data?.error || e.message, 'error'); }
    setConfirmId(null);
  };

  const handleToggle = async (id) => {
    try { await authAPI.toggleUser(id); load(); }
    catch(e) { toast(e.message,'error'); }
  };

  const ROLE_COLOURS = {
    super_admin: 'bg-red-50 text-red-700 border-red-200',
    admin:       'bg-orange-50 text-orange-700 border-orange-200',
    management:  'bg-purple-50 text-purple-700 border-purple-200',
    user:        'bg-blue-50 text-blue-700 border-blue-200',
    principal:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  };

  const uniqueRoles = [...new Set(users.map(u => u.role))].sort();

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className={`${inp} pl-8 w-52`} placeholder="Search name or email…"
              value={search} onChange={e=>setSearch(e.target.value)} />
          </div>
          <select className={inp} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)}>
            <option value="">All Roles</option>
            {uniqueRoles.map(r => <option key={r} value={r} className="capitalize">{r.replace(/_/g,' ')}</option>)}
          </select>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus size={14}/> Add User
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-medium uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Last Login</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No users found</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                    <span className="font-medium text-slate-700">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${ROLE_COLOURS[u.role] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {u.role?.replace(/_/g,' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-400 text-xs">
                  {u.last_login ? new Date(u.last_login).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <button onClick={()=>handleToggle(u.id)}
                    className={`transition ${u.is_active?'text-emerald-600 hover:text-emerald-800':'text-slate-400 hover:text-slate-600'}`}>
                    {u.is_active ? <ToggleRight size={20}/> : <ToggleLeft size={20}/>}
                  </button>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={()=>openEdit(u)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg" title="Edit">
                    <Edit2 size={14}/>
                  </button>
                  {u.role !== 'super_admin' && (
                    <button onClick={()=>setConfirmId(u.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg" title="Delete">
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit drawer */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{editing ? 'Edit User' : 'Add User'}</h3>
              <button onClick={()=>{setShowForm(false);setEditing(null);}} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-4">
              <FS label="Full Name *">
                <input className={fi} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Amit Acharya" />
              </FS>
              <FS label="Email Address *">
                <input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="user@thekulishschool.com"
                  disabled={!!editing} className={`${fi} ${editing?'bg-slate-50 text-slate-400':''}`} />
              </FS>
              <FS label="Role">
                <select className={fi} value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                  {roles.filter(r=>r.name!=='super_admin').map(r=>(
                    <option key={r.id} value={r.name}>{r.name.replace(/_/g,' ')} {r.description ? '— '+r.description : ''}</option>
                  ))}
                </select>
              </FS>
              {!editing && (
                <>
                  <FS label="Password">
                    <input type="password" className={fi} value={form.password}
                      onChange={e=>setForm(f=>({...f,password:e.target.value}))}
                      placeholder="Admin@123" />
                  </FS>
                  <p className="text-xs text-slate-400">Default password is <code className="bg-slate-100 px-1 rounded">Admin@123</code> if left blank. Change it later via User Access Control.</p>
                </>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={()=>{setShowForm(false);setEditing(null);}} className="flex-1 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
                <button onClick={save} disabled={saving} className="flex-1 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-medium disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <ConfirmDialog message="Delete this user? They will lose access immediately."
          onConfirm={()=>handleDelete(confirmId)} onCancel={()=>setConfirmId(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: SUBJECTS (with multi-grade assignment)
// ═══════════════════════════════════════════════════════════
const ALL_GRADES = ['Nursery','KG 1','KG 2','I','II','III','IV','V','VI','VI-ICSE','VII','VII-ICSE','VIII','VIII-ICSE'];

function SubjectsTab({ toast: showToast }) {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,   setEditId]   = useState(null);
  const [confirmId,setConfirmId]= useState(null);
  const [importing,setImporting]= useState(false);
  const [search,   setSearch]   = useState('');

  const empty = { name:'', code:'', grades:[] };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await schoolConfigAPI.getSubjects(); setItems(r.data); }
    catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(s =>
    !search ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.code||'').toLowerCase().includes(search.toLowerCase())
  );

  const toggleGrade = (g) => {
    setForm(f => ({
      ...f,
      grades: f.grades.includes(g) ? f.grades.filter(x=>x!==g) : [...f.grades, g],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { showToast('Subject name required', 'error'); return; }
    try {
      if (editId === 'new') await schoolConfigAPI.createSubject(form);
      else                  await schoolConfigAPI.updateSubject(editId, form);
      showToast(editId === 'new' ? 'Subject added' : 'Subject updated');
      setEditId(null); setForm(empty); load();
    } catch(e) { showToast(e.response?.data?.error||e.message,'error'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await schoolConfigAPI.importSubjects(fd); showToast(`Imported ${r.data.imported} subjects`); load(); }
    catch(e) { showToast(e.response?.data?.error||e.message,'error'); }
    finally { setImporting(false); e.target.value=''; }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input className={`${inp} pl-8 w-56`} placeholder="Search subjects…"
            value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async()=>{const r=await schoolConfigAPI.subjectTemplate();downloadBlob(r.data,'subjects_template.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Template
          </button>
          <label className={`flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer ${importing?'opacity-60':''}`}>
            <Upload size={14}/> {importing?'Importing…':'Import'}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport}/>
          </label>
          <button onClick={async()=>{const r=await schoolConfigAPI.exportSubjects();downloadBlob(r.data,'subjects_export.xlsx');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={14}/> Export
          </button>
          <button onClick={()=>{setForm(empty);setEditId('new');}}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={14}/> Add Subject
          </button>
        </div>
      </div>

      {/* Add / Edit form */}
      {editId !== null && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
          <p className="text-sm font-semibold text-slate-700">{editId==='new'?'Add Subject':'Edit Subject'}</p>
          <div className="grid grid-cols-2 gap-3">
            <FS label="Subject Name *">
              <input className={fi} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. English"/>
            </FS>
            <FS label="Code">
              <input className={fi} value={form.code||''} onChange={e=>setForm(f=>({...f,code:e.target.value}))} placeholder="e.g. ENG"/>
            </FS>
          </div>

          {/* Multi-grade selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Assign to Grades
                <span className="normal-case font-normal text-slate-400 ml-2">
                  {form.grades.length === 0
                    ? '(All grades — leave blank for global subjects)'
                    : `(${form.grades.length} of ${ALL_GRADES.length} selected)`}
                </span>
              </label>
              <div className="flex items-center gap-2">
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, grades: [...ALL_GRADES] }))}
                  className="text-xs font-medium text-primary-600 hover:text-primary-800 underline">
                  Select All
                </button>
                {form.grades.length > 0 && (
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, grades: [] }))}
                    className="text-xs font-medium text-red-500 hover:text-red-700 underline">
                    Clear All
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_GRADES.map(g => (
                <button key={g} type="button" onClick={()=>toggleGrade(g)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-full border transition-all ${
                    form.grades.includes(g)
                      ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-primary-300 hover:text-primary-600'
                  }`}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={()=>{setEditId(null);setForm(empty);}} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
            <button onClick={save} className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-medium uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Subject Name</th>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Grades Assigned</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading
              ? <tr><td colSpan={4} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : filtered.length === 0
                ? <tr><td colSpan={4} className="text-center py-8 text-slate-400">No subjects yet. Use "Add Subject" or "Import".</td></tr>
                : filtered.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-700">{s.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.code||'—'}</td>
                      <td className="px-4 py-3">
                        {s.grades?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {s.grades.map(g => (
                              <span key={g} className="px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-50 text-primary-700 border border-primary-100">
                                {g}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">All grades</span>
                        )}
                      </td>
                      <td className="px-4 py-3 flex gap-2">
                        <button
                          onClick={()=>{
                            setForm({ name:s.name, code:s.code||'', grades: s.grades||[] });
                            setEditId(s.id);
                          }}
                          className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={14}/></button>
                        <button onClick={()=>setConfirmId(s.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))
            }
          </tbody>
        </table>
      </div>

      {confirmId && (
        <ConfirmDialog message="Delete this subject?"
          onConfirm={async()=>{
            try { await schoolConfigAPI.deleteSubject(confirmId); showToast('Deleted'); load(); }
            catch(e) { showToast(e.message,'error'); }
            setConfirmId(null);
          }}
          onCancel={()=>setConfirmId(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: SUBJECT TEACHER ASSIGNMENT
// ═══════════════════════════════════════════════════════════
function SubjectTeacherTab({ toast: showToast }) {
  const [classes,     setClasses]     = useState([]);
  const [staff,       setStaff]       = useState([]);
  const [selClass,    setSelClass]    = useState('');
  const [subjects,    setSubjects]    = useState([]); // with assigned teacher_id
  const [assignments, setAssignments] = useState({}); // { subjectId: employeeId }
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([schoolConfigAPI.getClasses(), schoolConfigAPI.getStaff({})]);
      setClasses(c.data);
      setStaff(s.data.filter(e => e.is_active));
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadSubjects = useCallback(async () => {
    if (!selClass) { setSubjects([]); setAssignments({}); return; }
    setLoading(true);
    try {
      const r = await schoolConfigAPI.getSubjectTeachers(selClass);
      setSubjects(r.data.subjects);
      const map = {};
      r.data.subjects.forEach(s => { if (s.employee_id) map[s.id] = String(s.employee_id); });
      setAssignments(map);
    } catch { showToast('Failed to load subjects', 'error'); }
    finally { setLoading(false); }
  }, [selClass]);
  useEffect(() => { loadSubjects(); }, [loadSubjects]);

  const handleSave = async () => {
    if (!selClass) { showToast('Select a class first', 'error'); return; }
    setSaving(true);
    try {
      const asgs = subjects.map(s => ({
        subjectId: s.id,
        employeeId: assignments[s.id] ? parseInt(assignments[s.id]) : null,
      }));
      await schoolConfigAPI.saveSubjectTeachers(selClass, { assignments: asgs });
      showToast('Subject teacher assignments saved');
      loadSubjects();
    } catch(e) { showToast(e.response?.data?.error||e.message, 'error'); }
    finally { setSaving(false); }
  };

  const selClassObj = classes.find(c => String(c.id) === String(selClass));

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Class selector */}
      <div className="glass-card p-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Class / Section</label>
          <select className={`${inp} w-48`} value={selClass} onChange={e=>setSelClass(e.target.value)}>
            <option value="">Select class…</option>
            {classes.map(c=>(
              <option key={c.id} value={c.id}>
                {c.grade}{c.section ? ` — ${c.section}` : ''}
              </option>
            ))}
          </select>
        </div>
        {selClassObj && (
          <span className="text-xs text-slate-400 ml-2">
            Assign subject teachers for <strong className="text-slate-600">{selClassObj.grade}{selClassObj.section ? ` Section ${selClassObj.section}` : ''}</strong>
          </span>
        )}
        <button onClick={handleSave} disabled={!selClass||saving}
          className="ml-auto flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-sm">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
          {saving ? 'Saving…' : 'Save Assignments'}
        </button>
      </div>

      {!selClass ? (
        <div className="glass-card p-12 text-center">
          <Users size={36} className="text-slate-300 mx-auto mb-3"/>
          <p className="text-slate-400 font-medium">Select a class to assign subject teachers</p>
          <p className="text-xs text-slate-300 mt-1">Subjects are loaded from School Config → Subjects (grade-linked)</p>
        </div>
      ) : loading ? (
        <div className="glass-card p-12 flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-primary-400"/>
        </div>
      ) : subjects.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <BookOpen size={36} className="text-slate-300 mx-auto mb-3"/>
          <p className="text-slate-400 font-medium">No subjects found for this grade</p>
          <p className="text-xs text-slate-300 mt-1">Assign subjects to this grade in School Config → Subjects</p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
            <h3 className="text-sm font-bold text-slate-700">
              Subject Teacher Assignments — {selClassObj?.grade}{selClassObj?.section ? ` Section ${selClassObj.section}` : ''}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">{subjects.length} subject{subjects.length!==1?'s':''} · select a teacher for each</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 font-medium uppercase">
                <th className="px-5 py-3 text-left">Subject</th>
                <th className="px-5 py-3 text-left">Assigned Teacher</th>
                <th className="px-5 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {subjects.map(sub => (
                <tr key={sub.id} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <p className="font-medium text-slate-700">{sub.name}</p>
                    {sub.code && <p className="text-xs text-slate-400 font-mono">{sub.code}</p>}
                  </td>
                  <td className="px-5 py-3 w-72">
                    <select
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                      value={assignments[sub.id]||''}
                      onChange={e=>setAssignments(a=>({...a,[sub.id]:e.target.value}))}>
                      <option value="">— Not assigned —</option>
                      {staff.map(e=>(
                        <option key={e.id} value={e.id}>
                          {e.name}{e.designation ? ` (${e.designation})` : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3">
                    {assignments[sub.id] ? (
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Assigned</span>
                    ) : (
                      <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Not set</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════
// TAB: BIOMETRIC DEVICE (Realtime T304F / ZKTeco-compatible)
// ═══════════════════════════════════════════════════════════
function BiometricTab({ toast: showToast }) {
  const [devices,    setDevices]   = useState([]);
  const [mapping,    setMapping]   = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [activeView, setActiveView]= useState('devices'); // 'devices' | 'mapping'
  const [showForm,   setShowForm]  = useState(false);
  const [editing,    setEditing]   = useState(null);
  const [testStatus, setTestStatus]= useState({}); // { [deviceId]: {loading, result} }
  const [pullStatus, setPullStatus]= useState({}); // { [deviceId]: {loading, result} }
  const [form, setForm] = useState({ name:'Main Entrance', model:'Secureye S-FB3K', location:'', ipAddress:'10.10.74.93', port:'5005', commPassword:'2703', machineType:'S-FB3K', serialNo:'' });

  const serverBase = `${window.location.protocol}//${window.location.hostname}:5000`;

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try { const r = await biometricAPI.getDevices(); setDevices(r.data); }
    catch(e) { showToast(e.message,'error'); }
    finally { setLoading(false); }
  }, []);

  const loadMapping = useCallback(async () => {
    try { const r = await biometricAPI.getEmployeeMapping(); setMapping(r.data); }
    catch {}
  }, []);

  useEffect(() => { loadDevices(); loadMapping(); }, [loadDevices, loadMapping]);

  const save = async () => {
    try {
      const payload = { name:form.name, model:form.model, location:form.location, ipAddress:form.ipAddress, port:form.port, commPassword:parseInt(form.commPassword)||0, machineType:form.machineType, serialNo:form.serialNo };
      if (editing) await biometricAPI.updateDevice(editing.id, { ...payload, isActive:1 });
      else         await biometricAPI.addDevice(payload);
      showToast(editing ? 'Device updated' : 'Device added');
      setShowForm(false); setEditing(null);
      setForm({ name:'Main Entrance', model:'Secureye S-FB3K', location:'', ipAddress:'10.10.74.93', port:'5005', commPassword:'2703', machineType:'S-FB3K', serialNo:'' });
      loadDevices();
    } catch(e) { showToast(e.response?.data?.error||e.message,'error'); }
  };

  const handleTest = async (id) => {
    setTestStatus(p=>({...p,[id]:{loading:true}}));
    try {
      const r = await biometricAPI.testConnection(id);
      setTestStatus(p=>({...p,[id]:{loading:false,...r.data}}));
    } catch(e) { setTestStatus(p=>({...p,[id]:{loading:false,connected:false,error:e.response?.data?.error||'Failed'}})); }
  };

  const handlePull = async (id) => {
    setPullStatus(p=>({...p,[id]:{loading:true}}));
    try {
      const r = await biometricAPI.pullAttendance(id);
      setPullStatus(p=>({...p,[id]:{loading:false,...r.data}}));
      showToast(r.data.message);
      loadDevices();
    } catch(e) { setPullStatus(p=>({...p,[id]:{loading:false,error:e.response?.data?.error||'Failed'}})); showToast(e.response?.data?.error||'Pull failed','error'); }
  };

  const handleAutoMap = async (id) => {
    try {
      const r = await biometricAPI.autoMap(id);
      showToast(r.data.message);
      loadMapping();
    } catch(e) { showToast(e.response?.data?.error||'Auto-map failed','error'); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Tab switcher */}
      <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-100 shadow-sm w-fit">
        {[['devices','Devices'],['mapping','Employee Mapping']].map(([v,l])=>(
          <button key={v} onClick={()=>setActiveView(v)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeView===v?'bg-primary-600 text-white shadow-sm':'text-slate-600 hover:text-slate-800'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── DEVICES VIEW ── */}
      {activeView==='devices' && (
        <>
          {/* Integration method cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Method 1: USB Pull (Recommended for T304F in USB mode) */}
            <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</div>
                <p className="text-sm font-semibold text-slate-700">USB Cable Pull</p>
                <span className="text-[10px] bg-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">Best for T304F</span>
              </div>
              <p className="text-xs text-slate-500">Connect device via USB to this server PC. Uses SBXPCDLL.dll (already installed). No network config needed.</p>
              <p className="text-xs text-emerald-700 font-medium">→ Connect USB → click "Pull (USB)" button on device below</p>
            </div>

            {/* Method 2: USB File Import */}
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</div>
                <p className="text-sm font-semibold text-slate-700">USB / File Import</p>
                <span className="text-[10px] bg-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">Recommended</span>
              </div>
              <p className="text-xs text-slate-500">Export attendance from device to USB pendrive → upload file here. Works on all Realtime models regardless of network settings.</p>
              <div className="border-t border-emerald-200 pt-2">
                <p className="text-xs font-semibold text-emerald-700 mb-1">Device steps:</p>
                <p className="text-xs text-slate-600">Menu → USB Disk → Download Att Log → copy .dat/.xls to PC → upload below</p>
              </div>
            </div>

            {/* Method 3: ADMS Push */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0">3</div>
                <p className="text-sm font-semibold text-slate-700">ADMS Push</p>
                <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">Real-time</span>
              </div>
              <p className="text-xs text-slate-500">Device pushes each punch to our server automatically. Requires ADMS/Comm Settings on device.</p>
              <div className="border-t border-blue-200 pt-2">
                <p className="text-xs font-semibold text-blue-700 mb-0.5">Configure on device:</p>
                <p className="text-xs text-slate-600 font-mono">IP: {window.location.hostname}<br/>Port: 5000 · Path: /biometric</p>
              </div>
            </div>
          </div>

          {/* USB File Import section */}
          <div className="glass-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Upload size={15} className="text-emerald-600"/>
              <p className="text-sm font-semibold text-slate-700">Upload Device Attendance File</p>
            </div>
            <p className="text-xs text-slate-400">Supports: Realtime .dat/.xls/.csv export, ZKTeco log files, tab/space-delimited attendance files</p>
            <FileImportSection />
          </div>

          {/* Add/Edit form */}
          {showForm && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <p className="text-sm font-semibold text-slate-700">{editing?'Edit Device':'Add Device'}</p>
              <div className="grid grid-cols-2 gap-3">
                <FS label="Device Name"><input className={fi} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Main Entrance"/></FS>
                <FS label="Model"><input className={fi} value={form.model} onChange={e=>setForm(f=>({...f,model:e.target.value}))} placeholder="Realtime T304F"/></FS>
                <FS label="IP Address *"><input className={fi} value={form.ipAddress} onChange={e=>setForm(f=>({...f,ipAddress:e.target.value}))} placeholder="10.10.74.93"/></FS>
                <FS label="TCP Port"><input className={fi} value={form.port} onChange={e=>setForm(f=>({...f,port:e.target.value}))} placeholder="5005"/></FS>
                <FS label="Comm. Password">
                  <input className={fi} value={form.commPassword} onChange={e=>setForm(f=>({...f,commPassword:e.target.value}))} placeholder="0" type="number" min="0"/>
                  <p className="text-[10px] text-slate-400 mt-0.5">From InitSet → Communication Password (default 0, TKS device uses 2703)</p>
                </FS>
                <FS label="Machine Type">
                  <select className={fi} value={form.machineType} onChange={e=>setForm(f=>({...f,machineType:e.target.value}))}>
                    <option value="S-FB3K">S-FB3K (Secureye — TKS device)</option>
                    <option value="T304F">T304F (Realtime)</option>
                    <option value="S-922">S-922 (Secureye)</option>
                    <option value="SFB3">SFB3 (Secureye)</option>
                    <option value="MB160">MB160 (Secureye)</option>
                    <option value="">Auto-detect</option>
                  </select>
                </FS>
                <FS label="Location"><input className={fi} value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="e.g. Main Gate"/></FS>
                <FS label="Serial No. (optional)"><input className={fi} value={form.serialNo} onChange={e=>setForm(f=>({...f,serialNo:e.target.value}))} placeholder="102021110001316"/></FS>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>{setShowForm(false);setEditing(null);}} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
                <button onClick={save} className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              </div>
            </div>
          )}

          {/* Add button */}
          {!showForm && (
            <button onClick={()=>{setEditing(null);setForm({name:'Main Entrance',model:'Secureye S-FB3K',location:'',ipAddress:'10.10.74.93',port:'5005',commPassword:'2703',machineType:'S-FB3K',serialNo:''});setShowForm(true);}}
              className="flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              <Plus size={14}/> Add Device
            </button>
          )}

          {/* Device list */}
          {loading ? <div className="text-slate-400 py-6 text-center text-sm">Loading…</div>
            : devices.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <Fingerprint size={36} className="text-slate-300 mx-auto mb-2"/>
                <p className="text-sm text-slate-400">No devices configured. Add your Secureye S-FB3K above.</p>
              </div>
            ) : devices.map(dev => {
              const ts = testStatus[dev.id] || {};
              const ps = pullStatus[dev.id] || {};
              return (
                <div key={dev.id} className="glass-card p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Fingerprint size={16} className="text-primary-600"/>
                        <h3 className="text-sm font-bold text-slate-700">{dev.name}</h3>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${dev.is_active?'bg-emerald-50 text-emerald-700 border-emerald-200':'bg-slate-50 text-slate-400 border-slate-200'}`}>
                          {dev.is_active?'Active':'Inactive'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {dev.model}{dev.location ? ` · ${dev.location}` : ''}
                        {' · '}
                        <span className="font-mono">{dev.ip_address}:{dev.port}</span>
                        {dev.comm_password > 0 && <span className="ml-1 text-amber-600">🔑 pwd:{dev.comm_password}</span>}
                        {dev.machine_type  && <span className="ml-1 text-slate-400">· {dev.machine_type}</span>}
                        {dev.serial_no     && <span className="ml-1">· SN:{dev.serial_no}</span>}
                      </p>
                      {dev.last_sync_at && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Last sync: {new Date(dev.last_sync_at).toLocaleString('en-IN')} · {dev.last_sync_count} records
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={()=>{setEditing(dev);setForm({name:dev.name,model:dev.model||'',location:dev.location||'',ipAddress:dev.ip_address,port:String(dev.port),commPassword:String(dev.comm_password||0),machineType:dev.machine_type||'S-FB3K',serialNo:dev.serial_no||''});setShowForm(true);}}
                        className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Edit2 size={14}/></button>
                      <button onClick={async()=>{await biometricAPI.deleteDevice(dev.id);loadDevices();}}
                        className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg"><Trash2 size={14}/></button>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    {/* Test */}
                    <button onClick={()=>handleTest(dev.id)} disabled={ts.loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 bg-white hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-60">
                      {ts.loading ? <Loader2 size={12} className="animate-spin"/> : <Wifi size={12}/>}
                      {ts.loading ? 'Testing…' : 'Test Connection'}
                    </button>
                    {/* Pull via DLL/TCP — uses SBXPCDLL (Realtime/Secureye SDK) over TCP */}
                    <button onClick={async()=>{
                      setPullStatus(p=>({...p,[dev.id]:{loading:true}}));
                      try {
                        const r = await biometricAPI.pullViaDLL(dev.id);
                        setPullStatus(p=>({...p,[dev.id]:{loading:false,...r.data}}));
                        showToast(r.data.message);
                        loadDevices();
                      } catch(e) {
                        setPullStatus(p=>({...p,[dev.id]:{loading:false,error:e.response?.data?.error||'Pull failed'}}));
                        showToast(e.response?.data?.error||'Pull failed','error');
                      }
                    }} disabled={ps.loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-60 shadow-sm"
                      title="Pull via Realtime/Secureye DLL over TCP (uses SBXPCDLL with password)">
                      {ps.loading ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}
                      {ps.loading ? 'Pulling…' : 'Pull (TCP)'}
                    </button>
                    {/* Pull via USB — uses SBXPCDLL.dll, device must be connected via USB */}
                    <button onClick={async()=>{
                      setPullStatus(p=>({...p,[dev.id]:{loading:true}}));
                      try {
                        const r = await biometricAPI.pullViaUSB(dev.id);
                        setPullStatus(p=>({...p,[dev.id]:{loading:false,...r.data}}));
                        showToast(r.data.message);
                        loadDevices();
                      } catch(e) {
                        setPullStatus(p=>({...p,[dev.id]:{loading:false,error:e.response?.data?.error||'USB pull failed'}}));
                        showToast(e.response?.data?.error||'USB pull failed','error');
                      }
                    }} disabled={ps.loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-60"
                      title="Pull via USB cable (recommended for T304F)">
                      {ps.loading ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}
                      Pull (USB)
                    </button>
                    {/* Auto-map */}
                    <button onClick={()=>handleAutoMap(dev.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg transition-colors">
                      <Link size={12}/> Auto-Map Employees
                    </button>
                  </div>

                  {/* Test result */}
                  {/* SDK connected successfully (ZKTeco or Realtime DLL) */}
                  {ts.connected === true && ts.sdkAvailable && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs space-y-1">
                      <p className="font-semibold text-emerald-700 flex items-center gap-1.5">
                        <Wifi size={12}/>
                        {ts.sdkType === 'Realtime/Secureye DLL'
                          ? `Connected via Realtime SDK (SBXPCDLL) on port ${ts.usedPort}`
                          : `SDK Connected on port ${ts.usedPort}`}
                      </p>
                      {ts.message && <p className="text-emerald-600">{ts.message}</p>}
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {[
                          ['Device',       ts.deviceName],
                          ['SDK Type',     ts.sdkType],
                          ['Machine Type', ts.machineType],
                          ['Firmware',     ts.firmwareVersion],
                          ['Serial',       ts.serialNumber],
                          ['Users',        ts.userCount],
                          ['Log records',  ts.logCount],
                        ].filter(([,v])=>v!==undefined&&v!==null&&v!=='').map(([l,v])=>(
                          <div key={l}><span className="text-emerald-600">{l}:</span> <span className="font-medium text-emerald-800">{String(v)}</span></div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Reachable but ADMS-only (no SDK) */}
                  {ts.connected === true && ts.admsMode && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs space-y-3">
                      <p className="font-semibold text-amber-800 flex items-center gap-1.5">
                        <Wifi size={12}/> Device reachable · ADMS Push Mode
                      </p>
                      <p className="text-amber-700">{ts.message}</p>
                      {/* Configuration instructions */}
                      <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-1.5">
                        <p className="font-bold text-amber-800 text-[11px] uppercase tracking-wide">Configure device to push attendance:</p>
                        <ol className="text-amber-700 space-y-1 list-none">
                          <li className="flex gap-2"><span className="font-bold">1.</span><span>On device: <strong>Menu → Communication → ADMS</strong></span></li>
                          <li className="flex gap-2"><span className="font-bold">2.</span><span>Set <strong>Server IP</strong> = <code className="bg-amber-100 px-1 rounded font-mono">{window.location.hostname}</code></span></li>
                          <li className="flex gap-2"><span className="font-bold">3.</span><span>Set <strong>Server Port</strong> = <code className="bg-amber-100 px-1 rounded font-mono">5000</code></span></li>
                          <li className="flex gap-2"><span className="font-bold">4.</span><span>Enable ADMS and save — device will push punches automatically</span></li>
                        </ol>
                      </div>
                      {ts.lastAdmsPush ? (
                        <p className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1">
                          ✅ Last ADMS push received: {new Date(ts.lastAdmsPush).toLocaleString('en-IN')} · {ts.totalSynced} records synced
                        </p>
                      ) : (
                        <p className="text-slate-500 italic">No ADMS push received yet — configure the device with the settings above</p>
                      )}
                    </div>
                  )}
                  {/* Cannot connect at all */}
                  {ts.connected === false && !ts.admsMode && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 flex items-start gap-2">
                      <WifiOff size={13} className="flex-shrink-0 mt-0.5"/>
                      <span>{ts.error || 'Cannot connect — check IP, port, and network'}</span>
                    </div>
                  )}
                  {/* Cannot connect but ADMS instructions */}
                  {ts.connected === false && ts.admsMode && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs space-y-2">
                      <p className="font-semibold text-amber-800">SDK port unreachable — ADMS Push Mode required</p>
                      <p className="text-amber-700">{ts.error}</p>
                      <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-1">
                        <p className="font-bold text-amber-800 text-[11px] uppercase">Configure device to push:</p>
                        <p className="text-amber-700">Device: <strong>Menu → Communication → ADMS</strong></p>
                        <p className="text-amber-700">Server IP: <code className="bg-amber-100 px-1 rounded font-mono">{window.location.hostname}</code> · Port: <code className="bg-amber-100 px-1 rounded font-mono">5000</code></p>
                      </div>
                      {ts.lastAdmsPush && <p className="text-emerald-700">Last push: {new Date(ts.lastAdmsPush).toLocaleString('en-IN')}</p>}
                    </div>
                  )}
                  {/* Pull result */}
                  {ps.synced !== undefined && (
                    <div className={`border rounded-xl p-3 text-xs ${ps.synced>0?'bg-emerald-50 border-emerald-200 text-emerald-700':'bg-amber-50 border-amber-200 text-amber-700'}`}>
                      ✓ {ps.message}
                      {ps.unmatched > 0 && <span className="ml-2 text-amber-600">· {ps.unmatched} unmatched (use Employee Mapping tab)</span>}
                    </div>
                  )}
                </div>
              );
            })
          }
        </>
      )}

      {/* ── EMPLOYEE MAPPING VIEW ── */}
      {activeView==='mapping' && (
        <div className="glass-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
            <h3 className="text-sm font-bold text-slate-700">Employee → Device User ID Mapping</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Map each employee to their biometric device user ID. Use "Auto-Map" first — it matches employee_code to device user ID.
              Set manually for any that don't auto-match.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs text-slate-500 uppercase font-medium">
                  <th className="px-4 py-2.5 text-left">Employee</th>
                  <th className="px-4 py-2.5 text-left">Employee Id</th>
                  <th className="px-4 py-2.5 text-left">Biometric Code</th>
                  <th className="px-4 py-2.5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {mapping.map(emp => (
                  <tr key={emp.id} className={`hover:bg-slate-50 ${!emp.is_active?'opacity-40':''}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{emp.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{emp.employee_code||'—'}</td>
                    <td className="px-4 py-2.5">
                      <input
                        className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white w-32 font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
                        defaultValue={emp.biometric_uid||''}
                        placeholder="e.g. 1234"
                        onBlur={async (e) => {
                          const val = e.target.value.trim();
                          if (val !== (emp.biometric_uid||'')) {
                            try {
                              await biometricAPI.updateBiometricUid(emp.id, { biometricUid: val });
                              showToast('Saved');
                            } catch { showToast('Save failed','error'); }
                          }
                        }}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {emp.biometric_uid
                        ? <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1 w-fit mx-auto"><Link size={10}/> Linked</span>
                        : <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200 flex items-center gap-1 w-fit mx-auto"><Unlink size={10}/> Not set</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// TAB: STORAGE CONFIGURATION
// ═══════════════════════════════════════════════════════════
const PROVIDERS = [
  {
    key: 'server',
    label: 'Server Drive',
    desc: 'Store files on this server\'s local filesystem.',
    Icon: HardDrive,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    activeBorder: 'border-primary-500',
    activeBg: 'bg-primary-50',
  },
  {
    key: 'google_drive',
    label: 'Google Drive',
    desc: 'Store files in a shared Google Drive folder via OAuth.',
    Icon: Cloud,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    activeBorder: 'border-blue-500',
    activeBg: 'bg-blue-50',
  },
  {
    key: 'aws_s3',
    label: 'AWS S3 Bucket',
    desc: 'Store files in an Amazon S3 bucket.',
    Icon: Database,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    activeBorder: 'border-orange-500',
    activeBg: 'bg-orange-50',
  },
];

function StorageTab({ toast: showToast }) {
  const DEFAULTS = {
    provider:     'server',
    server:       { upload_path: '/uploads', max_file_size_mb: 50 },
    google_drive: { client_id: '', client_secret: '', folder_id: '' },
    aws_s3:       { bucket: '', region: 'ap-south-1', access_key_id: '', secret_access_key: '', folder_prefix: 'uploads/' },
  };

  const [cfg, setCfg]         = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    schoolConfigAPI.getStorageConfig()
      .then(r => setCfg({ ...DEFAULTS, ...r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setProvider = (p) => { setCfg(c => ({ ...c, provider: p })); setTestResult(null); };

  const patchProvider = (providerKey, patch) =>
    setCfg(c => ({ ...c, [providerKey]: { ...c[providerKey], ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await schoolConfigAPI.saveStorageConfig(cfg);
      showToast('Storage configuration saved', 'success');
      setTestResult(null);
    } catch(e) {
      showToast(e.response?.data?.error || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const providerConfig = cfg[cfg.provider];
      const r = await schoolConfigAPI.testStorageConfig({ provider: cfg.provider, config: providerConfig });
      setTestResult({ ok: true, msg: r.data.message });
    } catch(e) {
      setTestResult({ ok: false, msg: e.response?.data?.error || 'Connection test failed' });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 size={20} className="animate-spin text-slate-400" /></div>;

  const inp = 'w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300';

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-base font-semibold text-slate-800">Storage Configuration</h2>
        <p className="text-xs text-slate-500 mt-0.5">Choose where uploaded files and system-generated reports are stored.</p>
      </div>

      {/* Provider selection cards */}
      <div className="grid grid-cols-3 gap-3">
        {PROVIDERS.map(({ key, label, desc, Icon, color, bg, activeBorder, activeBg }) => {
          const active = cfg.provider === key;
          return (
            <button key={key} type="button" onClick={() => setProvider(key)}
              className={`relative flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all
                ${active ? `${activeBorder} ${activeBg} shadow-sm` : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`}>
              {active && (
                <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-primary-600">
                  <Check size={11} className="text-white" strokeWidth={3} />
                </span>
              )}
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${bg}`}>
                <Icon size={18} className={color} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{label}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Provider-specific fields */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
        {cfg.provider === 'server' && (
          <>
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><HardDrive size={14} /> Server Drive Settings</h3>
            <FS label="Upload Path">
              <input className={inp} value={cfg.server.upload_path}
                onChange={e => patchProvider('server', { upload_path: e.target.value })}
                placeholder="/uploads" />
              <p className="text-[11px] text-slate-400">Absolute or relative path on the server where files will be stored.</p>
            </FS>
            <FS label="Max File Size (MB)">
              <input type="number" min={1} max={500} className={inp} value={cfg.server.max_file_size_mb}
                onChange={e => patchProvider('server', { max_file_size_mb: Number(e.target.value) })} />
            </FS>
          </>
        )}

        {cfg.provider === 'google_drive' && (
          <>
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Cloud size={14} /> Google Drive Settings</h3>
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              Create OAuth 2.0 credentials in Google Cloud Console → APIs & Services → Credentials. Enable the Google Drive API for your project.
            </p>
            <FS label="Client ID">
              <input className={inp} value={cfg.google_drive.client_id}
                onChange={e => patchProvider('google_drive', { client_id: e.target.value })}
                placeholder="xxxxxxxxxxxx-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx.apps.googleusercontent.com" />
            </FS>
            <FS label="Client Secret">
              <input className={inp} value={cfg.google_drive.client_secret}
                onChange={e => patchProvider('google_drive', { client_secret: e.target.value })}
                placeholder="GOCSPX-xxxxxxxxxxxxxxxxxxxxxxxxx" />
            </FS>
            <FS label="Drive Folder ID">
              <input className={inp} value={cfg.google_drive.folder_id}
                onChange={e => patchProvider('google_drive', { folder_id: e.target.value })}
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
              <p className="text-[11px] text-slate-400">The ID from the folder's Google Drive URL (the part after /folders/).</p>
            </FS>
          </>
        )}

        {cfg.provider === 'aws_s3' && (
          <>
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Database size={14} /> AWS S3 Settings</h3>
            <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              Create an IAM user with s3:PutObject, s3:GetObject, s3:DeleteObject permissions on the target bucket, then use its access keys below.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <FS label="Bucket Name">
                <input className={inp} value={cfg.aws_s3.bucket}
                  onChange={e => patchProvider('aws_s3', { bucket: e.target.value })}
                  placeholder="my-school-files" />
              </FS>
              <FS label="Region">
                <select className={inp} value={cfg.aws_s3.region}
                  onChange={e => patchProvider('aws_s3', { region: e.target.value })}>
                  {['ap-south-1','ap-southeast-1','ap-southeast-2','ap-northeast-1',
                    'us-east-1','us-east-2','us-west-1','us-west-2',
                    'eu-west-1','eu-west-2','eu-central-1'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </FS>
            </div>
            <FS label="Access Key ID">
              <input className={inp} value={cfg.aws_s3.access_key_id}
                onChange={e => patchProvider('aws_s3', { access_key_id: e.target.value })}
                placeholder="AKIAIOSFODNN7EXAMPLE" autoComplete="off" />
            </FS>
            <FS label="Secret Access Key">
              <div className="relative">
                <input type={showSecret ? 'text' : 'password'} className={inp + ' pr-10'}
                  value={cfg.aws_s3.secret_access_key} autoComplete="new-password"
                  onChange={e => patchProvider('aws_s3', { secret_access_key: e.target.value })}
                  placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY" />
                <button type="button" onClick={() => setShowSecret(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </FS>
            <FS label="Folder Prefix (optional)">
              <input className={inp} value={cfg.aws_s3.folder_prefix}
                onChange={e => patchProvider('aws_s3', { folder_prefix: e.target.value })}
                placeholder="uploads/" />
              <p className="text-[11px] text-slate-400">Prefix path inside the bucket, e.g. <code>uploads/</code></p>
            </FS>
          </>
        )}
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`flex items-start gap-2 text-sm rounded-xl px-4 py-3 border ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {testResult.ok ? <Check size={15} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />}
          {testResult.msg}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        <button type="button" onClick={handleTest} disabled={testing || saving}
          className="flex items-center gap-2 px-5 py-2 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-colors">
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
      </div>
    </div>
  );
}

export default function SchoolConfiguration() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('staff');
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type='success') => { setToast({ msg, type }); }, []);

  // Super Admin only (admin role is treated as super_admin until migration runs)
  if (!['super_admin', 'admin'].includes(user?.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ShieldCheck size={48} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Super Admin access required</p>
          <p className="text-slate-400 text-sm mt-1">School configuration is accessible to Super Admins only.</p>
        </div>
      </div>
    );
  }

  const renderTab = () => {
    switch(activeTab) {
      case 'staff':        return <StaffTab toast={showToast} />;
      case 'students':     return <StudentsTab toast={showToast} />;
      case 'classes':      return <ClassesTab toast={showToast} />;
      case 'academic':     return <AcademicTab toast={showToast} />;
      case 'departments':  return <DepartmentsTab toast={showToast} />;
      case 'subjects':     return <SubjectsTab toast={showToast} />;
      case 'designations': return (
        <EnhancedListManagerTab label="Designation"
          fetchFn={schoolConfigAPI.getDesignations}
          createFn={d=>schoolConfigAPI.createDesignation(d)}
          updateFn={(id,d)=>schoolConfigAPI.updateDesignation(id,d)}
          deleteFn={id=>schoolConfigAPI.deleteDesignation(id)}
          importFn={fd=>schoolConfigAPI.importDesignations(fd)}
          exportFn={()=>schoolConfigAPI.exportDesignations()}
          templateFn={()=>schoolConfigAPI.designationTemplate()}
          fields={[{key:'name',label:'Designation Name',required:true},{key:'description',label:'Description'}]}
          toast={showToast} />
      );
      case 'roles':        return <RoleManagement />;
      case 'subjectteacher': return <SubjectTeacherTab toast={showToast} />;
      case 'telegram':     return <TelegramTab toast={showToast} />;
      case 'email':        return <EmailTab toast={showToast} />;
      case 'holidays':             return <HolidaysTab toast={showToast} />;
      case 'user_access':          return <UserAccessControl />;
      case 'storage':      return <StorageTab toast={showToast} />;
      default:             return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Settings size={24} className="text-primary-600" /> School Configuration
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage staff, students, subjects, and system settings · Admin only</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-slate-100 p-1.5 rounded-2xl">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={()=>setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition whitespace-nowrap
              ${activeTab===key ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'}`}>
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      <div>{renderTab()}</div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}
    </div>
  );
}
